import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, 
  ChevronDown, 
  ChevronRight,
  Search as SearchIcon, 
  CheckCircle2,
  Check,
  X,
  Eye,
  Share2,
  HelpCircle,
  Building2,
  Receipt,
  Shield,
  Sparkles,
  DollarSign,
  AlertTriangle,
  Info,
  FileText,
  ListChecks,
  MessageSquare,
  ArrowRight,
  Clock,
  BarChart2,
  ExternalLink
} from 'lucide-react';

// Brand colors
const BRAND = { navy: '#001F3F', green: '#10B981', red: '#FF4136', blue: '#1D4ED8', amber: '#F59E0B' };

// Design system (matched to homepage)
const CARD_STYLES = {
  borderRadius: '14px',
  border: '1px solid #E5E7EB',
  shadow: '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.03)',
};

// ===== CANONICAL DATA MODEL =====
// 4 Buckets only
const BUCKETS = [
  { id: 'government', title: 'Government and DMV', subtitle: 'Taxes, title, registration, state-required fees.', chip: 'Required', chipBg: '#E0F2FE', chipColor: '#0369A1', iconBg: '#E0F2FE', iconColor: '#0369A1', section: 'itemized' },
  { id: 'dealer', title: 'Dealer fees', subtitle: 'Doc fee, prep, advertising, markups, dealer charges.', chip: 'Common', chipBg: 'rgba(0,31,63,0.08)', chipColor: '#001F3F', iconBg: '#F3F4F6', iconColor: '#001F3F', section: 'itemized' },
  { id: 'finance', title: 'Finance office products', subtitle: 'GAP, warranty, maintenance, key replacement.', chip: 'Optional', chipBg: 'rgba(0,31,63,0.08)', chipColor: '#001F3F', iconBg: '#F3F4F6', iconColor: '#001F3F', section: 'addons' },
  { id: 'protection', title: 'Protection and add-ons', subtitle: 'Coatings, tint, etching, tracking, accessories.', chip: 'Optional', chipBg: 'rgba(0,31,63,0.08)', chipColor: '#001F3F', iconBg: '#F3F4F6', iconColor: '#001F3F', section: 'addons' },
];

const BUCKET_ICONS = { government: Building2, dealer: Receipt, finance: Shield, protection: Sparkles };

// Tag config
const TAG_CONFIG = {
  'required': { label: 'Required', bg: '#E0F2FE', color: '#0369A1' },
  'common': { label: 'Common', bg: '#E0F2FE', color: '#0369A1' },
  'negotiable': { label: 'Negotiable', bg: '#FEF3C7', color: '#92400E' },
  'avoid': { label: 'Avoid', bg: '#FEE2E2', color: '#991B1B' },
  'skip': { label: 'Skip', bg: '#FEE2E2', color: '#991B1B' },
  'overpriced': { label: 'Often overpriced', bg: '#FFEDD5', color: '#9A3412' },
  'shop': { label: 'Shop around', bg: '#DCFCE7', color: '#166534' },
};

// ===== CANONICAL ITEMS (30+ total) =====
const ITEMS = [
  // Government and DMV (5 items)
  { key: 'sales_tax', title: 'Sales tax', subtitle: 'State/local tax on vehicle purchase', bucket: 'government', tag: 'required', aliases: ['tax', 'state tax', 'local tax', 'vehicle tax', 'sales tax'], detail: {
    why: 'This is a pass-through government charge, but mistakes are common. The "taxable base" (what tax is applied to) can differ by state and deal structure.',
    typical_consumer: 'Varies by state/county and what is taxable.',
    typical_dealer: 'Same (dealer collects and remits).',
    say_polite: '"Can you show the taxable base and the exact tax rate used?"',
    say_firm: '"Please correct the tax calculation to match the proper rate and taxable base."',
    commonGotchas: [
      'Tax calculated on the wrong base (e.g., fees/add-ons included when they should not be, or trade-in credit handled incorrectly).',
      'Tax rate mismatch (county/city rate or delivery address rules can change the rate).',
      'Tax applied to optional add-ons you did not agree to.',
      '"Tax estimate" used as a placeholder—ask for the final calculation before signing.'
    ],
    redFlags: [
      'They will not show the taxable base, tax rate, and math line-by-line.',
      'Tax charged on items they cannot explain or will not itemize.',
      'Rate does not match where the vehicle will be registered.'
    ],
    whatToSay: {
      polite: "Can you show the taxable base and the exact tax rate used (state/county/city) and the math line-by-line?",
      firm: "Please correct the tax calculation to match the proper rate and taxable base. I am not signing until the numbers reconcile.",
      negotiationScripts: [
        "Please show: (1) taxable base, (2) tax rate, (3) tax total. I want to verify the math.",
        "Is the trade-in credit applied correctly for my state? Please show the calculation.",
        "If any add-on is optional, remove it first—then recalculate tax on the correct base."
      ],
      source_ids: []
    }
  } },
  { key: 'title_fee', title: 'Title fee', subtitle: 'State fee to transfer ownership', bucket: 'government', tag: 'required', aliases: ['title', 'title transfer', 'dmv title', 'certificate of title', 'ownership transfer'], detail: {
    why: 'The title is the legal ownership record. This is typically a state-set amount, but dealers sometimes bundle unrelated "processing" charges nearby.',
    typical_consumer: 'Varies by state (set by DMV/state).',
    typical_dealer: 'Same (pass-through).',
    say_polite: '"Is this exactly the state title fee for my registration address?"',
    say_firm: '"I need this itemized so I can confirm it is a government fee—not dealer markup."',
    commonGotchas: [
      'Lien recording or electronic title charges may appear separately—ask what each line item is.',
      'Confusing "title fee" with dealer documentation/processing fees (not the same).'
    ],
    redFlags: [
      'They cannot say which state form/DMV schedule the amount comes from.',
      '"Title fee" used as a bucket for non-government charges.'
    ],
    whatToSay: {
      polite: "Is this exactly the state title fee for my registration address? Can you show the DMV/state fee schedule?",
      firm: "I will pay required state title fees, but I need this itemized so I can confirm it is a government fee—not dealer markup.",
      negotiationScripts: [
        "Please separate state title fee from any dealer documentation/processing fees.",
        "If any portion is a dealer fee, label it clearly and tell me if it is optional or negotiable."
      ],
      source_ids: []
    }
  } },
  { key: 'registration_fee', title: 'Registration fee', subtitle: 'Annual vehicle registration', bucket: 'government', tag: 'required', aliases: ['registration', 'dmv registration', 'tag fee', 'plate registration', 'tags', 'sticker'], detail: {
    why: 'Registration is required to legally drive the vehicle. It often varies by state, vehicle type/weight/value, and can include multiple sub-fees.',
    typical_consumer: 'Varies by state and vehicle (DMV-set).',
    typical_dealer: 'Same (pass-through).',
    say_polite: '"Is this the final DMV registration amount or an estimate?"',
    say_firm: '"I need the registration line itemized and labeled as DMV pass-through."',
    commonGotchas: [
      '"Estimate" vs final DMV amount—final can change once DMV processes paperwork.',
      'New registration vs renewal vs transfer can change totals.',
      'Some states add separate fees (e.g., emissions, EV, local district fees) that show up inside registration.'
    ],
    redFlags: [
      'They will not tell you whether it is an estimate or final DMV charge.',
      'Registration line includes items they cannot name (ask for sub-fee breakdown).'
    ],
    whatToSay: {
      polite: "Is this the final DMV registration amount or an estimate? Can you break it into the DMV sub-fees?",
      firm: "I am fine paying required DMV fees, but I need the registration line itemized and labeled as DMV pass-through (estimate vs final).",
      negotiationScripts: [
        "If it is an estimate, what happens if the DMV total is lower—do I get a refund or credit?",
        "Please confirm whether this includes new plates, tag transfer, or renewal—so I am not double-charged."
      ],
      source_ids: []
    }
  } },
  { key: 'plate_fee', title: 'Plate / transfer fee', subtitle: 'License plate costs', bucket: 'government', tag: 'required', aliases: ['plates', 'plate fee', 'transfer fee', 'tag transfer', 'license plates', 'new plates'], detail: {
    why: 'This depends on whether you are getting new plates or transferring an existing plate. States handle this differently, so the key is clarity and itemization.',
    typical_consumer: 'Varies by state and whether plates are new vs transferred.',
    typical_dealer: 'Same (pass-through).',
    say_polite: '"Am I getting new plates or transferring my current plates?"',
    say_firm: '"Please correct this line to match what we are actually doing (new plates vs transfer)."',
    commonGotchas: [
      'Charged for new plates when you requested a transfer (or vice versa).',
      'Specialty/vanity plate costs mixed into a generic "plate fee."'
    ],
    redFlags: [
      'They cannot answer: "Am I getting new plates or transferring?"',
      'Plate fees bundled so you cannot tell what you are paying for.'
    ],
    whatToSay: {
      polite: "Am I getting new plates or transferring my current plates? Please itemize the exact DMV charges either way.",
      firm: "Please correct this line to match what we are actually doing (new plates vs transfer) and itemize it as DMV pass-through.",
      negotiationScripts: [
        "If I transfer plates, remove any new-plate issuance fees.",
        "If there is a specialty/vanity fee, list it separately so I can approve it."
      ],
      source_ids: []
    }
  } },
  { key: 'state_local_fees', title: 'State or local fees', subtitle: 'Other government-mandated fees', bucket: 'government', tag: 'required', aliases: ['county fees', 'local fees', 'city fees', 'regional fees', 'emissions', 'inspection', 'government fees'], detail: {
    why: 'This line is often a "catch-all." Some items are truly government pass-through (county fees, filing fees, emissions fees), but it can also be where non-government charges hide.',
    typical_consumer: 'Varies by state/county and transaction.',
    typical_dealer: 'Same for true government fees (pass-through).',
    say_polite: '"Can you itemize every fee inside state/local fees?"',
    say_firm: '"Anything dealer-set must be separated, explained, and approved in writing."',
    commonGotchas: [
      'Unclear "misc/state fees" with no breakdown—ask for sub-fees.',
      'Dealer-added charges labeled like government fees (e.g., "admin", "processing", "package")—not the same as DMV/state.'
    ],
    redFlags: [
      'They refuse to itemize each fee and identify which agency sets it.',
      'Any "state/local fee" that is described as dealer-set or "standard here."'
    ],
    whatToSay: {
      polite: "Can you itemize every fee inside state/local fees and tell me which ones are DMV/state-set vs dealer-set?",
      firm: "I will pay legitimate government pass-through fees, but anything dealer-set must be separated, explained, and approved in writing.",
      negotiationScripts: [
        "Please label each sub-fee as: DMV/state/county (pass-through) vs dealer fee (negotiable).",
        "If any part is dealer-set, remove it or move it to a clearly labeled dealer fee line so I can decide."
      ],
      source_ids: []
    }
  } },

  // Dealer fees (7 items - includes markup/watchlist)
  { key: 'doc_fee', title: 'Documentation fee (doc fee)', subtitle: 'Dealer paperwork processing', bucket: 'dealer', tag: 'common', aliases: ['doc fee', 'documentation', 'processing fee', 'dealer doc', 'admin fee', 'administrative fee'], detail: { why: 'Adds to OTD; usually not negotiable as a fee, but you negotiate price to offset.', sold: '"Standard / required."', typical_consumer: '$85–$999 (state dependent; some capped low)', typical_dealer: 'Same (some states cap at $85–$200)', say_polite: '"Ok — then reduce selling price so OTD stays the same."', say_firm: '"I\'m not increasing OTD for a doc fee."' } },
  { key: 'dealer_prep', title: 'Dealer prep / reconditioning', subtitle: 'Vehicle preparation before sale', bucket: 'dealer', tag: 'avoid', aliases: ['prep', 'pdi', 'reconditioning', 'recon', 'prep fee', 'inspection fee', 'dealer prep', 'pre-delivery'], detail: { why: 'Dealer cost of doing business; should not be extra.', sold: '"We have to prep it."', typical_consumer: '$0 (should be included)', typical_dealer: '$100–$500 (often junk fee)', say_polite: '"Please remove prep — that\'s included."', say_firm: '"No prep fee — adjust price."' } },
  { key: 'advertising_fee', title: 'Advertising fee', subtitle: 'Regional advertising costs', bucket: 'dealer', tag: 'avoid', aliases: ['ad fee', 'advertising', 'marketing fee', 'dealer advertising', 'regional ad'], detail: { why: 'Should be baked into price; commonly junk.', sold: '"Invoice deal requires it."', typical_consumer: '$0 (should be in price)', typical_dealer: '$100–$500+', say_polite: '"Please remove advertising — it\'s in MSRP/price already."', say_firm: '"We won\'t pay an advertising fee."' } },
  { key: 'market_adjustment', title: 'Market adjustment (markup)', subtitle: 'Pure profit above MSRP', bucket: 'dealer', tag: 'avoid', aliases: ['adm', 'markup', 'market value adjustment', 'dealer markup', 'addendum markup', 'price adjustment', 'market adjustment', 'adjustment'], detail: { why: 'Pure markup; increases taxes + interest. Adds nothing.', sold: '"Rare car / everyone pays it."', typical_consumer: '$0 (MSRP is fair)', typical_dealer: '$0–$10,000+ (dealer-imposed)', say_polite: '"I\'m not paying above MSRP — can you remove the markup?"', say_firm: '"If it stays, I\'m shopping elsewhere."' } },
  { key: 'dealer_addendum', title: 'Dealer addendum / package', subtitle: 'Pre-installed dealer add-ons', bucket: 'dealer', tag: 'avoid', aliases: ['addendum', 'dealer addendum', 'added equipment list', 'mandatory package', 'dealer package', 'accessory package'], detail: { why: 'Often unwanted items bundled at inflated prices.', sold: '"Already installed / can\'t remove."', typical_consumer: '$0 (negotiate removal)', typical_dealer: '$500–$3,000+', say_polite: '"Please itemize and remove what I don\'t want."', say_firm: '"Remove addendum or reduce price by full amount."' } },
  { key: 'delivery_destination', title: 'Destination / delivery charge', subtitle: 'Manufacturer shipping fee', bucket: 'dealer', tag: 'common', aliases: ['destination', 'freight', 'delivery charge', 'shipping fee', 'destination charge'], detail: { why: 'Legitimate manufacturer charge. Should match window sticker.', sold: 'Standard manufacturer fee.', typical_consumer: '$995–$1,995 (on window sticker)', typical_dealer: 'Same (verify matches sticker)', say_polite: '"Is this the same as the window sticker?"', say_firm: '"This must match the Monroney sticker exactly."' } },
  { key: 'dealer_handling', title: 'Dealer handling / conveyance', subtitle: 'Miscellaneous dealer fee', bucket: 'dealer', tag: 'negotiable', aliases: ['handling', 'conveyance', 'dealer fee', 'misc fee'], detail: { why: 'Catch-all fee. Often negotiable or junk.', sold: '"Standard fee."', typical_consumer: 'Varies', typical_dealer: '$100–$500', say_polite: '"What does this cover specifically?"', say_firm: '"Remove this or reduce price."' } },

  // Finance office products (8 items)
  { key: 'gap_insurance', title: 'GAP insurance', subtitle: 'Covers loan balance if totaled', bucket: 'finance', tag: 'shop', aliases: ['gap', 'guaranteed asset protection', 'loan gap', 'gap coverage', 'gap waiver', 'gap protection'], detail: {
    why: 'Dealer price is massively inflated; can get from insurer/credit union.',
    sold: '"You\'ll be upside down / required."',
    typical_consumer: '$20–$40/year via insurer',
    typical_dealer: '$500–$700 one-time',
    say_polite: '"I\'ll compare with my insurer/credit union first."',
    say_firm: '"Remove GAP — I\'m not buying it here."',
    legalNote: {
      headline: "GAP only applies on a total loss or unrecovered theft — it bridges the difference between your insurance payout and what you still owe.",
      plainEnglish: "If your car is totaled or stolen, your auto insurer typically pays Actual Cash Value (ACV), which can be less than your remaining loan or lease balance. GAP covers some or all of that remaining balance (depending on contract type and limits). It does not cover repairs, routine depreciation, or non-total losses.",
      variantsToKnow: [
        "GAP waiver (dealer/lender addendum): often higher limits; may reimburse your auto deductible (commonly up to $500–$1,000) and may cover negative equity rolled into the current loan (contract-dependent).",
        "Insurance GAP endorsement: usually cheaper on your auto policy but may be capped (e.g., a percentage of vehicle value) and typically does not cover deductible reimbursement or prior debt/negative equity.",
        "Lease GAP: often included in or required by a lease; may be automatic through the lessor."
      ],
      whatToLookForInPaperwork: [
        "Whether this is a waiver/addendum (contract) vs an insurance endorsement (policy)",
        "Coverage limit (e.g., full balance vs capped amount or percentage)",
        "Whether your collision deductible is reimbursed (and the dollar cap, if any)",
        "Whether negative equity is covered (and whether prior-loan debt is excluded)",
        "Exclusions: non-total losses, late/missed payments, commercial/ride-share use, unpaid fees",
        "Cancellation/refund section (pro-rata vs fees/penalties and where refunds are sent if financed)"
      ],
      priceBenchmarks: [
        "Dealership (one-time): typically $400–$900 rolled into the loan (some charge up to ~$1,000).",
        "Credit union/lender (one-time): often a flat fee (example: ~$499; commonly ~$500–$700).",
        "Insurer add-on: typically about $5–$10/month (roughly $20–$90/year).",
        "Lease GAP: often included in lease payments if required."
      ],
      disclaimer: "Educational info only — not legal, financial, or insurance advice. Terms, limits, and refund handling vary by provider, state, lender, and the specific contract."
    },
    commonGotchas: [
      "GAP only triggers on a total loss or unrecovered theft — it does not help with repairs or partial losses.",
      "Insurance pays ACV, not replacement cost; GAP is designed to cover the leftover loan/lease balance after the ACV payout (minus deductible).",
      "Contract type matters: dealer/lender waivers can differ from insurance endorsements in limits, deductible reimbursement, and what debt is eligible.",
      "Deductible reimbursement is not guaranteed: many waivers reimburse up to a cap (commonly $500–$1,000), but many insurance endorsements do not reimburse your deductible at all.",
      "Negative equity may or may not be covered: some waivers may cover negative equity rolled into the current loan, but many exclude any debt from prior loans or non-vehicle add-ons.",
      "Common exclusions: non-total losses, late/missed payments, late fees, unauthorized use (commercial/ride-share), and losses not covered by your primary auto policy.",
      "Coverage limits can be capped (e.g., a percentage of vehicle value or a dollar cap) — confirm the maximum benefit.",
      "Refunds can be delayed: cancellation is often prorated, but processing can be slow; if GAP is financed, refunds often go to the lender/loan balance.",
      "Duplicate coverage risk: leases may already include GAP, and your insurer or credit union may offer similar coverage cheaper.",
      "Bundling trick: \"Total Loss Protection,\" \"Vehicle Replacement Assist,\" or similar names may be bundled near GAP — confirm what each line item actually is."
    ],
    decisionGuide: [
      {
        question: "Is this a lease, or a loan?",
        guidance: "Leases often include or require GAP. If it is already included, avoid paying twice."
      },
      {
        question: "Do I actually have a gap?",
        guidance: "Estimate: gap = (loan/lease balance) − (insurance payout). Insurance payout is usually ACV minus deductible. If the gap is small or you have equity, you may not need GAP."
      },
      {
        question: "What type of GAP is it (waiver vs insurance endorsement)?",
        guidance: "Dealer/lender waivers often have different limits and may reimburse deductibles; insurance endorsements can be cheaper but may be capped and exclude more. Get the type and limits in writing."
      },
      {
        question: "Is the price fair vs alternatives?",
        guidance: "Dealers commonly charge a one-time fee in the hundreds. Compare against your credit union/lender flat fee and your auto insurer's monthly add-on before agreeing."
      },
      {
        question: "What exactly is excluded or capped?",
        guidance: "Confirm: limit/cap, deductible reimbursement (and cap), negative equity treatment, eligible use (personal vs commercial/ride-share), and whether late fees/missed payments are excluded."
      },
      {
        question: "What happens if I cancel or pay off the loan early?",
        guidance: "Check prorated refund rules and how refunds are handled if financed. Ask how to request cancellation and where the refund is sent."
      }
    ],
    redFlags: [
      "You are told GAP is required to get financing (it is typically optional; leases may require it but loans usually do not)",
      "Price is far above typical ranges (especially well above ~$600) without a clear explanation",
      "Seller cannot explain whether it is a waiver/addendum vs an insurance policy endorsement",
      "No clear limit/cap is disclosed (percentage cap or dollar cap hidden in paperwork)",
      "Deductible reimbursement implied but not written (or cap not stated)",
      "Negative equity treatment is vague or buried (prior-loan debt excluded without disclosure)",
      "Contract is not shown before signing or the GAP line item is added late in the paperwork",
      "No clear cancellation/refund policy is provided (or staff is evasive about refunds)",
      "You already have GAP via lease, insurer, or credit union and they still sell you another one",
      "They claim it covers everything (GAP has exclusions and only applies on total loss/theft)"
    ],
    whatToSay: {
      polite: "I understand GAP is optional. Please print the GAP contract/addendum and show the limit, exclusions, deductible reimbursement (if any), and the cancellation/refund policy. I'm going to compare this to my insurer and credit union before deciding.",
      firm: "Please remove GAP from the deal. If I decide I want it, I'll buy it separately after comparing options.",
      negotiationScripts: [
        "My credit union offers GAP for a flat fee. Can you match or reduce your price?",
        "Before I decide, I'm checking my auto policy — my insurer may add GAP for about $5–$10 per month.",
        "Is this a GAP waiver/addendum or an insurance endorsement? What are the limits and exclusions in writing?",
        "Does this reimburse my deductible? What is the exact deductible reimbursement cap?",
        "If I cancel or pay off early, what is the prorated refund policy and where is the refund sent if financed?"
      ]
    }
  } },
  { key: 'extended_warranty', title: 'Extended warranty / Vehicle service contract', subtitle: 'Optional breakdown coverage after factory warranty ends. Prices vary wildly — shop it like the car and treat first quotes as negotiable.', bucket: 'finance', tag: 'shop', aliases: ['Extended warranty', 'Vehicle service contract', 'Service contract', 'VSC', 'Extended service plan', 'Vehicle Service Agreement', 'Protection plan', 'Mechanical breakdown protection', 'Mechanical breakdown coverage', 'Dealer warranty (often third-party)'], detail: {
    disclaimer: "Educational only — not legal advice. Terms vary by provider, contract, and state. Always read the actual contract you're being asked to sign.",
    typicalPrice: {
      byPlanLevelDealer: [
        { coverageLevel: "Powertrain only", dealerRange: "$1,000–$1,800", notes: "Basic engine/transmission/drivetrain coverage. Often the only option for older/higher-mileage vehicles." },
        { coverageLevel: "Named component", dealerRange: "$1,500–$2,800", notes: "Covers only listed parts/systems. Anything not listed is not covered." },
        { coverageLevel: "Exclusionary (\"bumper-to-bumper\" style)", dealerRange: "$2,000–$4,500", notes: "Covers most mechanical/electrical items except a exclusions list. Still read the exclusions + definitions carefully." }
      ],
      example7yr100kExclusionary: [
        { vehicleClass: "Economy sedan", dealerRange: "$1,800–$2,500", thirdPartyOrCURange: "$1,200–$1,800", notes: "Lower repair risk = lower price." },
        { vehicleClass: "Midsize SUV", dealerRange: "$2,200–$3,200", thirdPartyOrCURange: "$1,500–$2,200", notes: "Electronics/AWD can raise cost." },
        { vehicleClass: "Full-size truck", dealerRange: "$2,500–$3,800", thirdPartyOrCURange: "$1,700–$2,500", notes: "Higher wear/powertrain stress." },
        { vehicleClass: "Luxury vehicle", dealerRange: "$3,200–$4,800", thirdPartyOrCURange: "$2,400–$3,500", notes: "High-tech + repair costs drive pricing." }
      ]
    },
    deductibleTypical: "$0–$250 typical. Ask if deductible is per visit or per repair item.",
    pricingNotes: [
      "Dealers often mark these up heavily — first quote is rarely the best.",
      "You usually do NOT have to buy it the same day you buy the car (especially OEM-backed plans).",
      "If you roll it into the loan, you also pay interest on it."
    ],
    why: "Dealers market these as \"extended warranties,\" but they're usually Vehicle Service Contracts (VSCs). Coverage is defined by the contract's exclusions, definitions (what counts as a 'breakdown'), and claim rules — not by what the finance manager says.",
    howItsSold: [
      "\"It's only a few dollars more per month.\"",
      "\"This price is only available today.\"",
      "\"It's basically bumper-to-bumper.\"",
      "\"Everyone gets it / it's recommended.\""
    ],
    redFlags: [
      "They won't give you the full contract/exclusions/cancellation terms to read before you sign.",
      "Pre-authorization required but they don't explain the process (claims can be denied if you don't call first).",
      "Reimbursement-only (you pay first) with vague timelines or unclear documentation rules.",
      "Shop restrictions (must use a network / \"approved\" shops) that reduce your options.",
      "Maintenance-records clause that can void coverage if you can't prove oil changes, etc.",
      "Big cancellation fee, unclear refund math, or vague language about refunds if it's financed.",
      "Deductible ambiguity (per visit vs per repair item).",
      "Arbitration clause you didn't notice (limits legal options)."
    ],
    whatToAsk: [
      "Is this a manufacturer-backed plan, a dealer plan, or a third-party service contract? Who is the obligor/administrator on the contract?",
      "Is it exclusionary or named-component? Show me the exclusions list and the definition of 'breakdown'.",
      "Is pre-authorization required before ANY work starts? What number does the shop call?",
      "Does the plan pay the repair shop directly, or do I pay and get reimbursed?",
      "Where can repairs be done (brand dealer, any licensed shop, ASE-certified)? Any restrictions?",
      "Is the deductible per visit or per repair item?",
      "Cancellation: Is it pro-rata? Any fees? If it's financed, does the refund go to the lender?"
    ],
    say_polite: "I'm open to coverage, but I'm not deciding today. Please print the full contract (administrator/obligor, exclusions, deductible rules, cancellation/refund terms). I'm going to compare quotes from other dealers and my credit union.",
    say_firm: "I'm not buying a service contract today. Please remove it from the deal.",
    edgeCases: [
      "Overlap risk: some plans run during the factory warranty — you may pay for coverage you already have.",
      "Older/high-mileage cars may only qualify for limited (powertrain) coverage.",
      "Claims can be denied for missing maintenance records or no pre-authorization depending on the contract."
    ]
  } },
  { key: 'maintenance_plan', title: 'Prepaid maintenance plan', subtitle: 'Prepaid oil changes and services', bucket: 'finance', tag: 'shop', aliases: ['maintenance plan', 'prepaid maintenance', 'service plan', 'oil changes plan'], detail: {
    why: "Can be convenient, but many prepaid plans are marked up and only make sense if you would do the same services at the same dealer anyway.",
    sold: "\"Locks in today's prices.\"",
    typical_consumer: "$200–$400 (pay as you go)",
    typical_dealer: "$500–$1,500",
    say_polite: "\"I'll handle maintenance myself.\"",
    say_firm: "\"Remove prepaid maintenance.\"",
    legalNote: {
      headline: "Prepaid maintenance is not a warranty",
      plainEnglish: "Prepaid maintenance typically covers scheduled services (like oil changes) for a set time/mileage. Value depends on what services are included, whether you can use it at other dealers, and whether unused value is refundable or transferable. If you do not service at that dealer or you sell early, you can overpay.",
      disclaimer: "Educational only. Not legal, insurance, or financial advice. Always rely on the exact plan contract you are offered.",
      source_ids: []
    },
    commonGotchas: [
      "Only covers very basic services (oil/filter) while you pay for everything else.",
      "Must service at selling dealer; limited transferability.",
      "Refund may be prorated or reduced by claims/fees.",
      "Not useful if you sell/trade early.",
      "Plan can be rolled into financing, increasing interest paid."
    ],
    decisionGuide: {
      whenMakesSense: [
        "You will keep the car long enough to use most services in the plan.",
        "You already plan to service at that dealer and the included services match your schedule.",
        "The plan price is close to the pay-as-you-go total for the same services."
      ],
      whenToSkip: [
        "You may sell/trade before using most services.",
        "Coverage is vague or limited to cheap services only.",
        "Refund/transfer rules are unclear or restrictive.",
        "The plan is being financed and you would pay interest on it."
      ],
      fairPriceGuidance: "Ask for a line-item list of included services with quantity limits and compare it to local service pricing. If the dealer cannot provide the exact included services in writing, decline.",
      cheaperBetterAlternatives: [
        "Pay as you go and shop independent service pricing.",
        "Use coupons/service specials if you prefer the dealer.",
        "Set aside a small maintenance fund monthly instead of prepaying."
      ],
      source_ids: []
    },
    redFlags: [
      "No written list of included services and limits.",
      "Presented as mandatory or bundled with unrelated add-ons.",
      "Refund/cancellation rules are unclear.",
      "Dealer refuses to remove it from the buyer's order."
    ],
    whatToSay: {
      polite: "Can you show exactly what services are included and the cancellation/refund rules in writing? I'll compare to pay-as-you-go.",
      firm: "Remove prepaid maintenance. I'm not adding that to the deal.",
      negotiationScripts: [
        "Itemize the services and quantities. If it is just oil changes, I'll pass.",
        "If I cancel within 30 days with no use, is it fully refundable? Put it in writing.",
        "Please reprint the buyer's order without prepaid maintenance."
      ],
      source_ids: []
    }
  } },
  { key: 'tire_wheel', title: 'Tire and wheel protection', subtitle: 'Coverage for tire/wheel damage', bucket: 'finance', tag: 'negotiable', aliases: ['tire and wheel', 'wheel and tire', 'road hazard', 'rim protection', 'tire coverage', 'curb damage'], detail: {
    why: "Can be useful in pothole/debris areas, but dealer pricing is often high and coverage details vary a lot by contract.",
    sold: "\"Potholes / curb damage.\"",
    typical_consumer: "$200–$400 aftermarket",
    typical_dealer: "$500–$1,000",
    say_polite: "\"I'll price this outside the deal.\"",
    say_firm: "\"Remove tire/wheel protection.\"",
    legalNote: {
      headline: "Tire & Wheel Protection (Road Hazard) — what it is",
      plainEnglish: "Tire & Wheel Protection (often called \"Road Hazard\") may pay to repair or replace a tire (and sometimes a wheel) if damaged by potholes, nails, glass, or debris. Contracts vary: some cover tires only, some cover wheels, and some add cosmetic wheel repair only in higher tiers.",
      disclaimer: "Educational only. Not legal, insurance, or financial advice. Always rely on the exact contract you are offered and confirm coverage in writing.",
      source_ids: []
    },
    commonGotchas: [
      "Cosmetic wheel repair is often excluded unless you buy a Plus/Optional tier.",
      "Wheel repair vs wheel replacement can be very different coverage — confirm which you get.",
      "Tread depth limits are common (worn tires may be excluded).",
      "Run-flat tires can have different rules or higher deductibles.",
      "Eligibility restrictions: often OEM size/spec only; aftermarket wheels/tires may be excluded.",
      "Pre-authorization may be required before you replace anything.",
      "Mounting/balancing/towing may be capped or excluded — verify in writing.",
      "Cancellation/refunds can be prorated and may go to the lender if financed."
    ],
    decisionGuide: {
      whenMakesSense: [
        "You drive where potholes/road debris are common and you have a history of flats or wheel damage.",
        "You have large wheels / low-profile tires where replacements are expensive.",
        "The contract explicitly includes wheel replacement (structural damage) and your tire/wheel setup is eligible."
      ],
      whenToSkip: [
        "It is tire-only and you can buy cheaper road-hazard coverage when you buy tires.",
        "Exclusions likely block your common scenario (tread depth, construction-zone carveouts, run-flat limits).",
        "Price is high and you are comfortable self-insuring."
      ],
      fairPriceGuidance: "Do not evaluate from the sales pitch. Evaluate the written contract: wheel replacement coverage, eligibility, deductible, and claim steps. If the dealer cannot provide the contract before signing, decline.",
      cheaperBetterAlternatives: [
        "Buy road-hazard coverage when you purchase tires (often cheaper than dealer plans).",
        "Self-insure: set aside a tire/wheel emergency fund.",
        "Basic flat repair is usually cheaper than long-term coverage if you rarely have flats."
      ],
      source_ids: []
    },
    redFlags: [
      "Won't provide the contract/sample terms before you sign.",
      "\"Covers everything\" language with no written exclusions/limits.",
      "Cosmetic coverage implied but not included in writing.",
      "No clarity on wheel replacement vs wheel repair.",
      "No clarity on deductible, per-claim caps, or authorization rules.",
      "Bundle pricing with unclear allocation (Key/Tire/Wheel) and no breakdown."
    ],
    whatToSay: {
      polite: "Can you show me the contract and point to wheel replacement, eligibility rules, and deductible? If it is not in writing, I am not buying it.",
      firm: "If this is optional, remove it now. If I change my mind, I will buy coverage elsewhere.",
      negotiationScripts: [
        "Show me wheel replacement language and eligibility in writing. If it is tire-only, remove it.",
        "What is the out-the-door price and term? If you cannot beat outside options, I will pass.",
        "If I cancel within 30 days with no claims, is it fully refundable? I need the written rule."
      ],
      source_ids: []
    }
  } },
  { key: 'key_replacement', title: 'Key / key fob replacement', subtitle: 'Coverage if you lose keys', bucket: 'finance', tag: 'overpriced', aliases: ['key protection', 'key fob', 'lost key', 'fob replacement', 'key coverage'], detail: {
    why: "Keys can be expensive, but key plans often have limits, deductibles, and strict claim rules.",
    sold: "\"Keys cost $500+ to replace.\"",
    typical_consumer: "$200–$400 (when needed)",
    typical_dealer: "$300–$600",
    say_polite: "\"I'll self-insure for key loss.\"",
    say_firm: "\"Remove key replacement.\"",
    legalNote: {
      headline: "Key/fob coverage is contract-driven",
      plainEnglish: "Key replacement plans may cover the cost of a new key/fob and programming if you lose or damage it. The real value depends on limits per claim, whether programming/towing is included, and what proof is required. Many people never use it, so compare the plan price to replacement cost and your personal risk.",
      disclaimer: "Educational only. Not legal, insurance, or financial advice. Always rely on the exact contract you are offered.",
      source_ids: []
    },
    commonGotchas: [
      "Deductibles or limits per claim may apply.",
      "Programming and towing may be excluded or capped.",
      "Must use specific providers or get pre-authorization.",
      "Damage vs loss may be treated differently.",
      "Refunds can be prorated or sent to lender if financed."
    ],
    decisionGuide: {
      whenMakesSense: [
        "You have a history of losing/damaging keys or share a vehicle with multiple drivers.",
        "The plan explicitly includes programming and any needed towing/lockout service.",
        "The price is low enough that one incident would roughly break even."
      ],
      whenToSkip: [
        "Price is close to or exceeds your expected replacement cost.",
        "Contract excludes programming/towing or requires steps you won't follow.",
        "You already have roadside/lockout coverage elsewhere."
      ],
      fairPriceGuidance: "Ask what exactly is covered (fob + mechanical key + programming) and whether there is a deductible. If it does not clearly cover programming, it is often not worth it.",
      cheaperBetterAlternatives: [
        "Self-insure: set aside a small key replacement fund.",
        "Check if your insurance/roadside plan includes lockout assistance.",
        "Ask about buying a spare key now at a lower cost than emergency replacement."
      ],
      source_ids: []
    },
    redFlags: [
      "Won't show contract terms before signing.",
      "Vague coverage like \"up to\" without clear caps and deductibles.",
      "Requires pre-authorization but does not explain how to get it.",
      "Excludes programming or does not mention it in writing."
    ],
    whatToSay: {
      polite: "What exactly does it cover in writing: fob, mechanical key, and programming? If programming isn't included, I'll pass.",
      firm: "Remove key coverage. I'm not financing a key plan into the loan.",
      negotiationScripts: [
        "Show me the deductible and claim limit in writing. If unclear, remove it.",
        "If I can cancel within 30 days for a full refund, put that in writing — otherwise remove it.",
        "I'll self-insure. Please reprint the buyer's order without key coverage."
      ],
      source_ids: []
    }
  } },
  { key: 'appearance_protection', title: 'Paint and appearance protection', subtitle: 'Sealant/"ceramic", PPF, interior protection, or a warranty', bucket: 'finance', tag: 'overpriced', aliases: ['appearance package', 'paint protection', 'ceramic', 'graphene', 'ppf', 'clear bra', 'interior protection', 'fabric protection', 'leather protection', 'stain protection', 'environmental protection', 'total protection'], detail: {
    why: "Often vague and bundled; value depends entirely on the exact product/installer and the written warranty terms.",
    sold: "\"Protects your investment.\"",
    typical_consumer: "Varies (detail shop quotes vary by scope)",
    typical_dealer: "$500–$1,500",
    say_polite: "\"What exactly is included?\"",
    say_firm: "\"Remove appearance package.\"",
    legalNote: {
      headline: "Paint & appearance packages can be product, warranty, or both",
      plainEnglish: "Appearance protection can mean a quick dealer sealant, a true ceramic coating, paint protection film (PPF), interior protection, and/or a reimbursement-style warranty for stains/chips/scratches. These are not the same thing. The only reliable way to evaluate it is to read the exact contract and scope in writing (brand, term, exclusions, limits, and claim steps).",
      disclaimer: "Educational only. Not legal, insurance, or financial advice. Always rely on the exact contract/addendum you are offered.",
      source_ids: []
    },
    commonGotchas: [
      "\"Ceramic/graphene\" can be marketing for a quick sealant rather than a multi-step professional coating.",
      "Bundles hide what you are paying for: paint + interior + warranty may be rolled into one line item.",
      "PPF scope matters: partial/front-only vs full front vs full vehicle changes value dramatically.",
      "Sales pitch may describe a warranty like it is a coating (reimbursement rules are not physical protection).",
      "Maintenance/inspection requirements can void coverage if you do not follow them (contract-specific).",
      "Exclusions can be broad (pre-existing damage, neglect, harsh chemicals, rock chips, etc.)."
    ],
    decisionGuide: {
      whenMakesSense: [
        "You want PPF on high-impact zones (front bumper/hood/mirrors) and the scope is clearly shown in writing.",
        "You are comparing against reputable local detailer quotes and the dealer price is competitive.",
        "The warranty terms are clear, usable, and match the damage you actually worry about (stains, chips, etching)."
      ],
      whenToSkip: [
        "Dealer cannot provide the exact brand/product name, term, and exclusions in writing.",
        "The \"ceramic\" pitch is vague and sounds like a quick upsell rather than a real install.",
        "The warranty has strict requirements you will not follow (inspections, approved products, etc.).",
        "Price is high and you can get equal/better work from a reputable detailer for similar money."
      ],
      fairPriceGuidance: "Do not buy based on labels like \"ceramic\" or \"shield.\" Require the brand, scope map (for PPF), term, exclusions, claim limits, and maintenance requirements in writing. If any of that is missing, decline.",
      cheaperBetterAlternatives: [
        "Get 2–3 quotes from reputable detailers for real ceramic/PPF with clear scope and prep steps.",
        "Buy targeted PPF (front/high-impact) instead of a vague full bundle.",
        "DIY sealants for short-term protection if you mainly want easier washing."
      ],
      source_ids: []
    },
    redFlags: [
      "No contract/addendum shown before you sign.",
      "\"Ceramic/graphene\" marketed with no brand/spec sheet.",
      "Product described vaguely (\"shield/guard/armor\") with no written scope.",
      "Sales pitch makes it sound like a coating, but it is mainly a reimbursement warranty.",
      "PPF sold without a coverage map (partial vs full front vs full vehicle).",
      "Exclusions so broad they likely block your real-world use case.",
      "Maintenance/inspection rules that you will not realistically follow (coverage can be voided).",
      "Dealer says it is mandatory or \"already installed\" but will not provide proof/work order.",
      "High-pressure tactics or refusal to remove the add-on from the buyer's order.",
      "Low payout caps/per-panel limits not disclosed up front (contract-specific).",
      "No clear cancellation/refund rule in writing."
    ],
    whatToSay: {
      polite: "Please show me the contract and scope in writing: brand, term, exclusions, and claim limits. If it is not itemized, I am not buying it.",
      firm: "Remove the appearance package. I will handle paint/interior protection outside the deal.",
      negotiationScripts: [
        "What is the exact brand and term? If you cannot provide it in writing, remove it.",
        "If this is PPF, show the coverage map. If it is not real film with a scope, I am not paying for it.",
        "If you insist it is installed, show a dated work order and product documentation. Otherwise remove the charge."
      ],
      source_ids: []
    }
  } },
  { key: 'lease_wear_tear', title: 'Lease wear & tear coverage', subtitle: 'Coverage for lease return damage', bucket: 'finance', tag: 'negotiable', aliases: ['wear and tear', 'lease protection', 'excess wear', 'lease return coverage'], detail: { why: 'Can be useful for lessees; price varies.', sold: '"Avoid turn-in charges."', typical_consumer: 'Varies', typical_dealer: '$300–$800', say_polite: '"I\'ll consider this separately."', say_firm: '"Remove wear and tear coverage."' } },
  { key: 'credit_life', title: 'Credit life / disability insurance', subtitle: 'Pays loan if you can\'t', bucket: 'finance', tag: 'skip', aliases: ['credit life', 'credit insurance', 'disability insurance', 'payment protection'], detail: { why: 'Rarely worth it; term life is better.', sold: '"Protects your family."', typical_consumer: 'Get term life instead', typical_dealer: '$500–$2,000', say_polite: '"I have life insurance already."', say_firm: '"Remove credit life insurance."' } },

  // Protection and add-ons (20+ items)
  { key: 'vin_etching', title: 'VIN etching', subtitle: 'Etches VIN on windows', bucket: 'protection', tag: 'overpriced', aliases: ['etching', 'vin etch', 'anti-theft etch', 'glass etching', 'vin etching', 'window etching'], detail: {
    why: 'Usually high-margin for something you can do cheaply yourself. Etching is a deterrent/ID marker, not tracking or recovery.',
    sold: '"Insurance discount / theft deterrent."',
    typical_consumer: '$10–$30 DIY kit',
    typical_dealer: '$200–$400+',
    say_polite: '"Remove VIN etch — I can do this myself."',
    say_firm: '"No — take it off or reduce price by same amount."',
    legalNote: {
      headline: 'VIN etching is not tracking or recovery',
      plainEnglish: "VIN etching marks the vehicle identification number on the glass. It may deter some theft and can help identify stolen parts, but it does not locate or recover a stolen car. Dealers sometimes bundle etching with alarms or trackers—make sure you know exactly what you are being charged for.",
      disclaimer: "Educational only. Not legal, insurance, or financial advice. Always rely on the contract/addendum and your insurer's written guidance.",
      source_ids: []
    },
    commonGotchas: [
      'Bundle confusion: an item called "security package" may include etching, alarms, and/or tracking—demand an itemized breakdown.',
      'Markup risk: etching kits are inexpensive; dealer pricing can be many times higher than DIY.',
      'Insurance discount assumptions: some insurers discount, many do not; confirm directly with your insurer.',
      'Already included: some vehicles already have strong theft deterrents; etching may add little value.',
      'Refund/cancellation: if financed, refunds (if allowed) may be prorated and applied to the loan balance.'
    ],
    decisionGuide: {
      whenMakesSense: [
        'You want a basic theft-deterrent marker and the price is modest.',
        'It is included at no charge or at a near-DIY price.',
        'You verified a real insurance discount in writing that materially offsets cost.'
      ],
      whenToSkip: [
        'Price is high for a simple glass marking.',
        'Dealer will not itemize what you are buying.',
        'They imply it is tracking/recovery (it is not).',
        'Your insurer will not confirm any discount (or discount is trivial).'
      ],
      fairPriceGuidance: 'DIY kits are inexpensive. If a dealer price is hundreds of dollars, treat it as negotiable and compare to DIY or decline.',
      cheaperBetterAlternatives: [
        'DIY VIN etching kit.',
        'Steering wheel lock / visible deterrent.',
        'Use factory security/telematics if your vehicle already has it.'
      ],
      source_ids: []
    },
    redFlags: [
      'Presented as mandatory.',
      'Dealer says it is required by law.',
      'No itemized breakdown provided.',
      'Claims it is tracking/recovery or "guaranteed" theft protection.',
      'Added late in paperwork without clear consent.'
    ],
    whatToSay: {
      polite: "I understand what it is, but I do not want VIN etching at this price. Please remove it and reprint the buyer's order.",
      firm: "I am not buying VIN etching. Remove it or reduce the price by the full amount.",
      negotiationScripts: [
        "Please remove VIN etching. If I want it, I can do it myself for a fraction of the cost.",
        "Show me the exact addendum and itemized components—no bundle labels.",
        "If you say it saves me money, show the insurer discount in writing; otherwise remove it.",
        "I will buy the car at the same out-the-door price without this add-on."
      ],
      source_ids: []
    }
  } },
  { key: 'nitrogen_tires', title: 'Nitrogen filled tires', subtitle: 'Nitrogen instead of air', bucket: 'protection', tag: 'skip', aliases: ['nitrogen', 'nitrogen tires', 'nitrogen fill', 'n2', 'n2 tires'], detail: {
    why: 'Nitrogen can reduce pressure swings slightly, but for most drivers the real key is checking tire pressure regularly. Regular air already contains mostly nitrogen.',
    sold: '"Better mileage / tire life."',
    typical_consumer: '$5–$10/tire retail',
    typical_dealer: '$150–$200/vehicle',
    say_polite: '"Remove nitrogen — standard air is fine."',
    say_firm: '"Not paying for nitrogen."',
    legalNote: {
      headline: 'Nitrogen is not a magic upgrade',
      plainEnglish: "Nitrogen inflation is sometimes sold as a premium add-on. The practical benefit is usually small for normal driving—consistent tire pressure checks matter far more for tire life and fuel economy. If the dealer is charging for nitrogen, treat it as optional and negotiable.",
      disclaimer: 'Educational only. Not financial advice. Always prioritize correct tire pressure per the door-jamb placard.',
      source_ids: []
    },
    commonGotchas: [
      '"Lifetime refills" may only apply at that dealer/location.',
      'Some dealers charge for nitrogen even though you can top off with regular air anywhere.',
      'If you mix air and nitrogen later, the claimed advantage becomes even smaller.',
      'Pressure losses usually come from temperature changes or slow leaks—nitrogen does not fix a leak.',
      'Often bundled into a package to make removal harder—ask for itemization.'
    ],
    decisionGuide: {
      whenMakesSense: [
        'It is included at no additional charge.',
        'You track pressure carefully and want a small stability benefit (edge case).'
      ],
      whenToSkip: [
        'Any meaningful added fee for something you can replace with free air.',
        'Dealer cannot explain exactly what you are paying for (just marketing claims).'
      ],
      fairPriceGuidance: 'If there is a line-item charge, treat it as negotiable and usually skip.',
      cheaperBetterAlternatives: [
        'Use regular air and check pressure monthly.',
        'Buy a tire pressure gauge and keep pressures correct.',
        'Fix slow leaks promptly instead of paying for nitrogen.'
      ],
      source_ids: []
    },
    redFlags: [
      'Charged as mandatory.',
      'Bundled with other add-ons and not itemized.',
      'Claims of major MPG gains or dramatic tire-life guarantees.',
      'Refuses to remove the fee when requested.'
    ],
    whatToSay: {
      polite: "I am fine with regular air. Please remove any nitrogen tire fee and reprint the buyer's order.",
      firm: 'Remove the nitrogen charge. I am not paying for it.',
      negotiationScripts: [
        'If it is optional, delete the charge. I will maintain tire pressure myself.',
        'Show me where this is required—if it is not required, remove it.',
        'If you want me to keep it, make it no-cost; otherwise remove it.'
      ],
      source_ids: []
    }
  } },
  { key: 'paint_protection', title: 'Paint protection / sealant', subtitle: 'Coating to protect paint', bucket: 'protection', tag: 'overpriced', aliases: ['paint protection', 'paint sealant', 'clear coat', 'paint coating'], detail: { why: 'Dealer version often basic; aftermarket is better.', sold: '"Protects finish."', typical_consumer: '$50–$150 DIY', typical_dealer: '$300–$800', say_polite: '"I\'ll handle paint protection myself."', say_firm: '"Remove paint protection."' } },
  { key: 'ceramic_coating', title: 'Ceramic coating', subtitle: 'Professional paint protection', bucket: 'protection', tag: 'shop', aliases: ['ceramic', 'ceramic coat', 'coating', 'nano coating'], detail: { why: 'Can be worth it if professional; dealer often overpriced.', sold: '"Ultimate protection."', typical_consumer: '$500–$1,500 professional shop', typical_dealer: '$1,000–$2,500', say_polite: '"I\'ll get this from a detailer."', say_firm: '"Remove ceramic coating."' } },
  { key: 'ppf', title: 'Paint protection film (PPF)', subtitle: 'Clear film on paint', bucket: 'protection', tag: 'shop', aliases: ['ppf', 'clear bra', 'paint film', 'xpel', '3m film'], detail: { why: 'Legitimate product but shop prices vary widely.', sold: '"Prevents rock chips."', typical_consumer: '$500–$2,000 at specialty shop', typical_dealer: '$1,000–$3,000+', say_polite: '"I\'ll get PPF from a specialist."', say_firm: '"Remove PPF from the deal."' } },
  { key: 'fabric_protection', title: 'Fabric / interior protection', subtitle: 'Spray-on fabric protector', bucket: 'protection', tag: 'overpriced', aliases: ['fabric protection', 'scotchgard', 'fabric guard', 'interior guard', 'stain protection'], detail: { why: 'Cheap to DIY; dealer version is same product.', sold: '"Protects from stains."', typical_consumer: '$15–$30 can of Scotchgard', typical_dealer: '$200–$500', say_polite: '"I\'ll apply fabric protector myself."', say_firm: '"Remove fabric protection."' } },
  { key: 'rustproofing', title: 'Rustproofing / undercoating', subtitle: 'Anti-rust treatment', bucket: 'protection', tag: 'overpriced', aliases: ['undercoating', 'rust proofing', 'rust protection', 'corrosion protection', 'rustproofing'], detail: { why: 'Often unnecessary (modern cars); dealer markup 2–4x.', sold: '"Protects resale / must-have."', typical_consumer: '$250–$400 at shop', typical_dealer: '$800–$1,000+', say_polite: '"I\'ll get this aftermarket if needed."', say_firm: '"Remove it."' } },
  { key: 'theft_tracking', title: 'Theft recovery / tracking', subtitle: 'GPS tracking device', bucket: 'protection', tag: 'overpriced', aliases: ['lojack', 'karr', 'swat', 'theft recovery', 'tracking', 'gps', 'vehicle locator', 'tracker', 'stolen vehicle recovery', 'svr', 'kahu', 'spider', 'calamp', 'ituran'], detail: {
    why: 'Can be redundant; markup heavy. Often "already installed."',
    sold: '"Required / already installed."',
    typical_consumer: '$100–$200 aftermarket',
    typical_dealer: '$500–$1,000+',
    say_polite: '"Please itemize and remove tracking add-on."',
    say_firm: '"I\'m not paying for a tracker."',
    legalNote: {
      headline: "Theft Deterrent / Vehicle Recovery Disclaimer",
      plainEnglish: "Theft deterrent/vehicle recovery add-ons are optional protections sold in dealership finance offices to help prevent or recover from car theft. They include VIN etching, which marks the vehicle's ID number on windows as a deterrent, and GPS trackers like LoJack that use location tech for recovery. These apply to both new and used cars, often as standalone items or bundles, but effectiveness varies and many cars already have factory anti-theft features.",
      disclaimer: "This information is for educational purposes only and is not legal or financial advice. Consult professionals for your specific situation.",
      source_ids: []
    },
    commonGotchas: [
      "Already included by manufacturer (factory alarms/telematics)",
      "Ongoing subscription fees (GPS)",
      "Cancellation/refund terms",
      "Transferability",
      "Requirements that can void claims (police report timing, keys left in car, etc.)"
    ],
    decisionGuide: {
      whenMakesSense: [
        "High-theft area or vehicle model (e.g., popular trucks/SUVs).",
        "No factory telematics (e.g., older used cars without OnStar).",
        "Qualifies for insurance discount (5-25% on comprehensive).",
        "New car with long ownership plan; GPS aids quick recovery.",
        "Bundle if components add unique value beyond OEM."
      ],
      whenToSkip: [
        "Low-theft risk area or secure parking.",
        "Factory anti-theft already included (e.g., immobilizer, alarm).",
        "Dealer price inflated; DIY/cheaper options available.",
        "Ongoing GPS fees not worth it for short-term ownership.",
        "Insurance doesn't discount or covers theft adequately."
      ],
      fairPriceGuidance: "VIN etch: Dealer one-time $200-400; often overpriced vs DIY $20-30. GPS tracker: Hardware $500-1500 + monthly/annual sub $10-50 (varies; some like LoJack no sub for 3-5 yrs). Alarm: One-time $200-500 if not factory. Bundle: $800-2000 prorated; depends on components.",
      cheaperBetterAlternatives: [
        "DIY VIN etch kits: $20-30; same deterrent effect.",
        "Insurer anti-theft discounts: 5-25% on comp; no added cost if qualifying device installed.",
        "Apple AirTag: $30 one-time; uses Find My network for tracking, no sub.",
        "Other trackers (e.g., Tracki): $50-150 + $10-20/mo; portable GPS.",
        "OEM subscriptions (e.g., OnStar): $15-50/mo; built-in recovery."
      ],
      source_ids: [
        1,
        19,
        21,
        46,
        47,
        50,
        56,
        60,
        62,
        69,
        70,
        72,
        79,
        90,
        96,
        115
      ]
    },
    redFlags: [
      "Dealer claims \"required by law\" (false).",
      "No itemized breakdown of bundle components.",
      "Price > typical benchmarks (e.g., etch >$300).",
      "Hidden ongoing subs not disclosed upfront.",
      "Non-cancellable/non-refundable terms.",
      "Exaggerated effectiveness claims without sources.",
      "Already factory-installed (duplication).",
      "No transfer to next owner.",
      "Vague coverage: theft only, no burglary.",
      "High-pressure sales in F&I office.",
      "No police report timing details (can void).",
      "Brand claims generalized (e.g., LoJack only for LoJack).",
      "Added without consent (illegal).",
      "No insurance discount confirmation.",
      "\"Free\" but baked into vehicle price."
    ],
    whatToSay: {
      polite: "Insurance gives me a discount for anti-theft; this bundle duplicates that. Waive it?",
      firm: "My car already has factory GPS—why add another tracker? Let's take off the $800 fee.",
      negotiationScripts: [
        "This VIN etch seems high at $300—I've seen DIY kits for $20. Can you remove it or match that?",
        "My car already has factory GPS—why add another tracker? Let's take off the $800 fee.",
        "Insurance gives me a discount for anti-theft; this bundle duplicates that. Waive it?",
        "LoJack is $700, but reviews say no sub needed. Confirm no hidden fees and drop to $500?",
        "This protection package includes etch I can do myself. Break it out and remove extras.",
        "Cancellation terms? If non-refundable, I'll pass—too risky for used car."
      ],
      source_ids: [
        19,
        46,
        47,
        50,
        56,
        60,
        62
      ]
    }
  } },
  { key: 'alarm_system', title: 'Alarm / security system', subtitle: 'Aftermarket alarm', bucket: 'protection', tag: 'overpriced', aliases: ['security system', 'alarm', 'anti-theft system', 'starter interrupt', 'car alarm'], detail: { why: 'Most cars have factory alarm; aftermarket redundant.', sold: '"Extra protection."', typical_consumer: '$100–$300 aftermarket', typical_dealer: '$400–$800', say_polite: '"My car has factory alarm."', say_firm: '"Remove aftermarket alarm."' } },
  { key: 'window_tint', title: 'Window tint', subtitle: 'Aftermarket window film', bucket: 'protection', tag: 'negotiable', aliases: ['tint', 'tinting', 'window film', 'window tinting'], detail: { why: 'Legitimate but shop around; dealer often marks up.', sold: '"Reduces heat / UV."', typical_consumer: '$150–$400 at tint shop', typical_dealer: '$300–$700', say_polite: '"I\'ll get tint from a specialist."', say_firm: '"Remove tint from deal."' } },
  { key: 'door_edge_guards', title: 'Door edge guards', subtitle: 'Protective strips', bucket: 'protection', tag: 'skip', aliases: ['door guards', 'edge guards', 'door protection', 'door edge'], detail: { why: 'Cheap accessory sold at huge markup.', sold: '"Prevents dings."', typical_consumer: '$10–$30 DIY', typical_dealer: '$100–$200', say_polite: '"I\'ll add these myself."', say_firm: '"Remove door guards."' } },
  { key: 'wheel_locks', title: 'Wheel locks', subtitle: 'Anti-theft lug nuts', bucket: 'protection', tag: 'negotiable', aliases: ['locking lug nuts', 'lug locks', 'wheel lock kit'], detail: { why: 'Minor deterrent; cheap at auto store.', sold: '"Prevents wheel theft."', typical_consumer: '$20–$50', typical_dealer: '$75–$150', say_polite: '"I\'ll buy wheel locks separately."', say_firm: '"Remove wheel locks."' } },
  { key: 'all_weather_mats', title: 'All-weather floor mats', subtitle: 'Rubber floor mats', bucket: 'protection', tag: 'negotiable', aliases: ['floor mats', 'all weather mats', 'rubber mats', 'weathertech', 'cargo mats'], detail: { why: 'Useful but often cheaper online.', sold: '"Protects carpet."', typical_consumer: '$100–$200 online', typical_dealer: '$200–$400', say_polite: '"Can you include mats at cost?"', say_firm: '"I\'ll buy mats elsewhere."' } },
  { key: 'cargo_accessories', title: 'Cargo tray / accessories', subtitle: 'Trunk liner and accessories', bucket: 'protection', tag: 'negotiable', aliases: ['cargo tray', 'trunk liner', 'cargo net', 'accessory package'], detail: { why: 'Useful but markup varies.', sold: '"Protects cargo area."', typical_consumer: '$50–$150 online', typical_dealer: '$100–$300', say_polite: '"What\'s included in this?"', say_firm: '"Remove or discount accessories."' } },
  { key: 'dealer_accessories', title: 'Dealer-installed accessories', subtitle: 'Dealer add-on items', bucket: 'protection', tag: 'negotiable', aliases: ['dealer options', 'installed options', 'aftermarket add-ons', 'dealer accessories', 'port installed'], detail: { why: 'Often marked up; can decline non-factory items.', sold: '"Already installed."', typical_consumer: 'Varies', typical_dealer: 'Marked up 50–200%', say_polite: '"Please itemize each accessory."', say_firm: '"Remove items I didn\'t request."' } },
  { key: 'pinstriping', title: 'Pinstriping / graphics', subtitle: 'Decorative stripes', bucket: 'protection', tag: 'skip', aliases: ['pinstripe', 'stripes', 'graphics', 'decals'], detail: { why: 'Usually unwanted; pure profit.', sold: '"Enhances appearance."', typical_consumer: '$0 (don\'t want it)', typical_dealer: '$100–$300', say_polite: '"Please remove pinstriping."', say_firm: '"Remove or credit full amount."' } },
  { key: 'windshield_protection', title: 'Windshield protection', subtitle: 'Chip repair coverage', bucket: 'protection', tag: 'overpriced', aliases: ['windshield', 'glass protection', 'chip repair', 'windshield coverage'], detail: { why: 'Often covered by insurance; dealer version overpriced.', sold: '"Chips happen."', typical_consumer: 'Often free via insurance', typical_dealer: '$200–$500', say_polite: '"My insurance covers this."', say_firm: '"Remove windshield protection."' } },
  { key: 'dent_protection', title: 'Paintless dent repair coverage', subtitle: 'Dent repair coverage', bucket: 'protection', tag: 'overpriced', aliases: ['dent repair', 'pdr', 'ding protection', 'dent coverage'], detail: { why: 'Pay per repair is usually cheaper.', sold: '"Door dings happen."', typical_consumer: '$50–$150 per repair', typical_dealer: '$300–$600 coverage', say_polite: '"I\'ll handle dents as needed."', say_firm: '"Remove dent protection."' } },
  { key: 'clear_coat', title: 'Clear coat protection', subtitle: 'Additional clear coat', bucket: 'protection', tag: 'skip', aliases: ['clear coat', 'clearcoat', 'extra clear'], detail: { why: 'Factory clear coat is sufficient.', sold: '"Extra protection layer."', typical_consumer: '$0 (not needed)', typical_dealer: '$200–$500', say_polite: '"Factory clear coat is fine."', say_firm: '"Remove clear coat add-on."' } },
  { key: 'splash_guards', title: 'Splash guards / mud flaps', subtitle: 'Wheel well protection', bucket: 'protection', tag: 'negotiable', aliases: ['mud flaps', 'splash guards', 'mud guards'], detail: { why: 'Cheap accessory; big markup.', sold: '"Protects paint from debris."', typical_consumer: '$30–$80', typical_dealer: '$100–$250', say_polite: '"I\'ll add these myself."', say_firm: '"Remove splash guards."' } },
  { key: 'protection_bundle', title: 'Total protection bundle', subtitle: 'Bundled protection products', bucket: 'protection', tag: 'avoid', aliases: ['total protection', 'platinum package', 'gold package', 'protection bundle', 'complete protection'], detail: { why: 'Vague bundle of marked-up items.', sold: '"Best value / everything included."', typical_consumer: 'Price items separately', typical_dealer: '$1,500–$4,000', say_polite: '"Please itemize each component."', say_firm: '"Remove the bundle — I don\'t want it."' } },
];

// Search function - matches title and aliases
const searchItems = (query, items) => {
  if (!query.trim()) return items;
  const q = query.toLowerCase().trim();
  return items.filter(item => {
    if (item.title.toLowerCase().includes(q)) return true;
    if (item.subtitle.toLowerCase().includes(q)) return true;
    if (item.aliases?.some(a => a.toLowerCase().includes(q))) return true;
    return false;
  });
};

// Get items by bucket
const getItemsByBucket = (bucketId) => ITEMS.filter(i => i.bucket === bucketId);

// Receipt Illustration (lighter weight)
// Icon tile - clean, no decorative dots
const IconTile = ({ children, size = 'md', className = '' }) => {
  const sizeStyles = {
    sm: { width: 32, height: 32 },
    md: { width: 40, height: 40 },
    lg: { width: 112, height: 112 }
  };
  const s = sizeStyles[size] || sizeStyles.md;
  return (
    <div className={`rounded-xl flex items-center justify-center flex-shrink-0 ${className}`} style={{ width: s.width, height: s.height, backgroundColor: '#F3F4F6', border: '1px solid #E5E7EB' }}>
      {children}
    </div>
  );
};

const DealCheckIcon = () => (
  <div style={{ 
    width: 148, 
    height: 148, 
    borderRadius: '24px',
    backgroundColor: '#FFFFFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    border: '1px solid rgba(0,31,63,0.12)',
    boxShadow: '0 4px 16px rgba(0,31,63,0.08)',
    animation: 'dealCheckFadeIn 300ms ease-out forwards',
    opacity: 0,
    transform: 'scale(0.98)'
  }}>
    <style>{`
      @keyframes dealCheckFadeIn {
        from { opacity: 0; transform: scale(0.98); }
        to { opacity: 1; transform: scale(1); }
      }
    `}</style>
    <svg viewBox="0 0 32 32" style={{ width: 96, height: 96 }}>
      {/* Shield/badge shape */}
      <path d="M16 3 L26 7 L26 14 C26 20 22 25 16 28 C10 25 6 20 6 14 L6 7 Z" fill="none" stroke="#001F3F" strokeWidth="1.8" strokeLinejoin="round"/>
      {/* Checkmark */}
      <path d="M11 16 L14.5 19.5 L21 12" fill="none" stroke="#001F3F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  </div>
);

// Premium Bucket Card with hover/press affordance for category rows
const BucketCard = ({ bucket, itemCount, selectedCount, isExpanded, onClick }) => {
  const Icon = BUCKET_ICONS[bucket.id];
  // Chip styling: Required=green, Common=blue, Optional=muted red (warning-lite)
  const getChipStyle = (chipType) => {
    if (chipType === 'Required') return { backgroundColor: '#DCFCE7', color: '#166534' };
    if (chipType === 'Common') return { backgroundColor: '#E0F2FE', color: '#0369A1' };
    if (chipType === 'Optional') return { backgroundColor: '#FEF2F2', color: '#B91C1C' };
    return { backgroundColor: '#F3F4F6', color: '#6B7280' };
  };
  const chipStyle = getChipStyle(bucket.chip);
  return (
    <>
      <style>{`
        .category-row-interactive {
          cursor: pointer;
          transition: transform 200ms ease, box-shadow 200ms ease, background-color 150ms ease, border-color 150ms ease;
        }
        .category-row-interactive:hover {
          box-shadow: 0 6px 16px rgba(0,31,63,0.12) !important;
          transform: translateY(-2px);
          background-color: #FAFBFC !important;
          border-color: ${BRAND.navy} !important;
        }
        .category-row-interactive:active {
          transform: translateY(0) scale(0.995);
          box-shadow: 0 2px 8px rgba(0,31,63,0.10) !important;
        }
      `}</style>
      <button onClick={onClick} className="w-full p-4 text-left category-row-interactive"
        style={{ 
          backgroundColor: '#FFFFFF', 
          border: `1.5px solid ${isExpanded ? BRAND.navy : 'rgba(0,31,63,0.25)'}`,
          borderLeft: isExpanded ? `3px solid ${BRAND.navy}` : `1.5px solid rgba(0,31,63,0.25)`,
          borderRadius: '14px',
          boxShadow: '0 2px 6px rgba(0,31,63,0.06)'
        }}
        data-testid={`bucket-card-${bucket.id}`}>
        <div className="flex items-center gap-3">
          <IconTile size="md">
            <Icon className="w-5 h-5" style={{ color: BRAND.navy }} strokeWidth={1.8} />
          </IconTile>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5 flex-wrap">
              <span className="text-sm font-semibold" style={{ color: BRAND.navy }}>{bucket.title}</span>
              <span className="px-2 py-0.5 text-xs font-medium rounded-full" style={chipStyle}>{bucket.chip}</span>
            </div>
            <p className="text-xs" style={{ color: '#6B7280' }}>{bucket.subtitle}</p>
            {selectedCount > 0 && <span className="inline-block mt-1 px-2 py-0.5 text-xs font-semibold rounded-full" style={{ backgroundColor: '#E0F2FE', color: '#0369A1' }}>{selectedCount} selected</span>}
          </div>
          <div className="flex-shrink-0">{isExpanded ? <ChevronDown className="w-5 h-5" style={{ color: BRAND.red }} strokeWidth={2.5} /> : <ChevronRight className="w-5 h-5" style={{ color: BRAND.red }} strokeWidth={2.5} />}</div>
        </div>
      </button>
    </>
  );
};

// Clean Checklist Item
const ChecklistItem = ({ item, isSelected, onToggle }) => {
  const tagStyle = TAG_CONFIG[item.tag] || {};
  return (
    <button onClick={() => onToggle(item.key)} className="w-full flex items-center gap-3 p-3 rounded-lg text-left transition-all duration-150"
      style={{ backgroundColor: isSelected ? '#F0F9FF' : '#FAFBFC', border: isSelected ? '1px solid #0284C7' : '1px solid #E5E7EB' }}
      data-testid={`checklist-item-${item.key}`}>
      <div className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 border transition-all ${isSelected ? 'bg-sky-500 border-sky-500' : 'bg-white border-gray-300'}`}>
        {isSelected && <Check className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium" style={{ color: BRAND.navy }}>{item.title}</span>
          <span className="px-1.5 py-0.5 text-xs font-medium rounded" style={{ backgroundColor: tagStyle.bg, color: tagStyle.color }}>{tagStyle.label}</span>
        </div>
        <p className="text-xs mt-0.5" style={{ color: '#6B7280' }}>{item.subtitle}</p>
      </div>
    </button>
  );
};

// Bucket Section
const BucketSection = React.forwardRef(({ bucket, items, selectedItems, onToggle, isExpanded }, ref) => {
  if (!isExpanded) return null;
  return (
    <div ref={ref} className="mt-3 space-y-2 scroll-mt-4" data-testid={`bucket-section-${bucket.id}`}>
      {items.map(item => <ChecklistItem key={item.key} item={item} isSelected={selectedItems.has(item.key)} onToggle={onToggle} />)}
    </div>
  );
});

// Review Card
const ReviewCard = ({ item, onReview, onRemove }) => {
  const tagStyle = TAG_CONFIG[item.tag] || TAG_CONFIG['common'];
  return (
    <div className="p-4 rounded-xl" style={{ backgroundColor: '#FFFFFF', border: '1px solid rgba(0,31,63,0.08)' }} data-testid={`review-card-${item.key}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-sm font-medium" style={{ color: BRAND.navy }}>{item.title}</span>
            <span className="px-2 py-0.5 text-xs font-medium rounded-full" style={{ backgroundColor: tagStyle.bg, color: tagStyle.color }}>{tagStyle.label}</span>
          </div>
          <p className="text-xs" style={{ color: '#6B7280' }}>{item.subtitle}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button onClick={() => onReview(item)} className="px-3 py-1.5 rounded-lg text-xs font-medium hover:opacity-90" style={{ backgroundColor: BRAND.navy, color: '#FFFFFF' }} data-testid={`review-btn-${item.key}`}>
            <Eye className="w-3.5 h-3.5 inline mr-1" />Review
          </button>
          <button onClick={() => onRemove(item.key)} className="px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-gray-100" style={{ backgroundColor: '#F3F4F6', color: '#6B7280' }} data-testid={`remove-btn-${item.key}`}>
            <X className="w-3.5 h-3.5 inline mr-1" />Remove
          </button>
        </div>
      </div>
    </div>
  );
};

// Enhanced Item Detail View
// GAP Detail View - Clean copy polish version
const GAPDetailView = ({ item, onBack }) => {
  const tagStyle = TAG_CONFIG[item.tag] || TAG_CONFIG['common'];
  
  // Hardcoded red flags as per spec (9 items exactly)
  const gapRedFlags = [
    "They say GAP is required to get financing (it's typically optional; some leases may include/require it).",
    "The price is high for what it is (especially well above ~$600–$700) without a clear explanation.",
    "They won't clearly explain what type it is (insurance add-on vs waiver/addendum) and what it covers.",
    "No written cancellation/refund policy — or they're vague about how refunds work if it's financed.",
    "The GAP line item isn't clearly shown on the buyer's order/finance agreement until late in the paperwork.",
    "They claim it 'covers everything' without showing limits, exclusions, or the actual contract language.",
    "You already have GAP through your insurer/credit union/lease — and they still try to sell you another one.",
    "You have strong equity (big down payment/short term) but they push it anyway without doing the math.",
    "They bundle it with other add-ons so you can't see a clean stand-alone price."
  ];
  
  return (
    <div data-testid="item-detail-view" style={{ backgroundColor: '#FFFFFF' }}>
      {/* Back button */}
      <button onClick={onBack} className="flex items-center gap-1 text-sm font-medium mb-5 hover:underline" style={{ color: '#0284C7' }} data-testid="back-to-selected">
        <ChevronLeft className="w-4 h-4" />Back to selected items
      </button>
      
      {/* 1. HEADER */}
      <div className="flex items-start justify-between mb-2">
        <div>
          <h3 className="text-xl font-bold mb-1" style={{ color: BRAND.navy }}>{item.title}</h3>
          <p className="text-sm" style={{ color: '#6B7280', lineHeight: '1.6' }}>
            Covers the difference between your insurance payout (ACV) and what you still owe if the car is totaled or stolen.
          </p>
        </div>
        <span className="px-3 py-1.5 text-xs font-medium rounded-lg flex-shrink-0" style={{ backgroundColor: '#F3F4F6', color: '#6B7280', border: '1px solid #E5E7EB' }}>
          {tagStyle.label}
        </span>
      </div>
      
      {/* Divider */}
      <div style={{ height: '1px', backgroundColor: '#E5E7EB', margin: '20px 0' }} />
      
      {/* 2. TYPICAL PRICE */}
      <div className="mb-6 p-5 rounded-2xl" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <div className="flex items-center gap-2 mb-4">
          <DollarSign className="w-4 h-4" style={{ color: BRAND.navy }} />
          <h4 className="text-sm font-semibold" style={{ color: BRAND.navy }}>Typical price</h4>
        </div>
        
        <div className="space-y-5">
          {/* Insurer row */}
          <div className="pb-4" style={{ borderBottom: '1px solid #F3F4F6' }}>
            <p className="text-xs font-medium uppercase tracking-wide mb-1" style={{ color: '#9CA3AF' }}>Added to your auto policy (insurer)</p>
            <p className="text-sm font-medium mb-2" style={{ color: '#374151' }}>$2–$7/mo (about $20–$90/yr; many people see ~$24–$40/yr)</p>
            <p className="text-xs" style={{ color: '#9CA3AF', lineHeight: '1.5' }}>
              Usually added to the same full-coverage policy you already need for this car. Easier to cancel later once you're not upside-down anymore.
            </p>
          </div>
          
          {/* Dealer row */}
          <div>
            <p className="text-xs font-medium uppercase tracking-wide mb-1" style={{ color: '#9CA3AF' }}>At the dealership (rolled into the loan)</p>
            <p className="text-sm font-medium mb-2" style={{ color: '#374151' }}>$500–$900 one-time</p>
            <p className="text-xs" style={{ color: '#9CA3AF', lineHeight: '1.5' }}>
              If you finance it, you'll also pay interest on that cost.
            </p>
          </div>
        </div>
      </div>
      
      {/* 3. WHAT IT IS */}
      <div className="mb-6 p-5 rounded-2xl" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <div className="flex items-center gap-2 mb-3">
          <Info className="w-4 h-4" style={{ color: BRAND.navy }} />
          <h4 className="text-sm font-semibold" style={{ color: BRAND.navy }}>What it is</h4>
        </div>
        <p className="text-sm" style={{ color: '#5C6370', lineHeight: '1.7' }}>
          GAP (Guaranteed Asset Protection) is optional coverage that helps if your car is totaled or stolen and you owe more than your auto insurer pays. It can cover some or all of the remaining loan or lease balance that's left after the insurance payout.
        </p>
      </div>
      
      {/* 4. HOW IT WORKS */}
      <div className="mb-6 p-5 rounded-2xl" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <div className="flex items-center gap-2 mb-3">
          <HelpCircle className="w-4 h-4" style={{ color: BRAND.navy }} />
          <h4 className="text-sm font-semibold" style={{ color: BRAND.navy }}>How it works</h4>
        </div>
        <p className="text-sm mb-4" style={{ color: '#5C6370', lineHeight: '1.7' }}>
          Auto insurance usually pays the car's Actual Cash Value (ACV) — what the car is worth at the time of the loss — minus your deductible.
          <br /><br />
          If you still owe more than that payout, you're responsible for the difference to the lender/lessor unless you have GAP (limits vary by plan).
          <br /><br />
          GAP only applies on a total loss or unrecovered theft — it does not cover repairs or partial damage.
        </p>
        {/* Example callout */}
        <div className="p-3 rounded-lg" style={{ backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB' }}>
          <p className="text-xs" style={{ color: '#6B7280', lineHeight: '1.6' }}>
            <span className="font-semibold">Example:</span> You owe $30,000. The car is totaled. Insurance values it at $25,000 and pays $24,500 after your $500 deductible. Your lender still wants $30,000 — without GAP you'd owe $5,500 out of pocket. GAP can cover some or all of that difference (up to plan limits).
          </p>
        </div>
      </div>
      
      {/* 5. WHY IT MATTERS */}
      <div className="mb-6 p-5 rounded-2xl" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle className="w-4 h-4" style={{ color: '#D97706' }} />
          <h4 className="text-sm font-semibold" style={{ color: BRAND.navy }}>Why it matters</h4>
        </div>
        <p className="text-sm" style={{ color: '#5C6370', lineHeight: '1.7' }}>
          Dealer GAP is often marked up and commonly rolled into the loan — which means you may pay interest on it for years. In many cases, an insurer or credit union can add similar coverage for less, and it's typically simpler to cancel later once your loan balance drops below the car's value.
        </p>
      </div>
      
      {/* 6. HOW IT IS SOLD */}
      <div className="mb-6 p-5 rounded-2xl" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <div className="flex items-center gap-2 mb-3">
          <MessageSquare className="w-4 h-4" style={{ color: '#6B7280' }} />
          <h4 className="text-sm font-semibold" style={{ color: BRAND.navy }}>How it is sold</h4>
        </div>
        <div className="pl-4" style={{ borderLeft: '3px solid #D1D5DB' }}>
          <p className="text-sm italic" style={{ color: '#5C6370', lineHeight: '1.7' }}>"You'll be upside down / required."</p>
        </div>
      </div>
      
      {/* 7. RED FLAGS - Stacked rows, NO bullets */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-4">
          <AlertTriangle className="w-4 h-4" style={{ color: '#DC2626' }} />
          <h4 className="text-sm font-semibold" style={{ color: '#DC2626' }}>Red flags</h4>
        </div>
        <div className="space-y-3">
          {gapRedFlags.map((flag, i) => (
            <div key={i} className="flex items-start gap-3 p-4 rounded-xl" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', borderLeft: '3px solid #F87171' }}>
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#F87171' }} />
              <p className="text-sm" style={{ color: '#5C6370', lineHeight: '1.6' }}>{flag}</p>
            </div>
          ))}
        </div>
      </div>
      
      {/* 8. WHAT TO SAY */}
      <div className="mb-6">
        <h4 className="text-sm font-semibold mb-4" style={{ color: BRAND.navy }}>What to say</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Polite card */}
          <div className="p-4 rounded-xl" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#16A34A' }} />
              <p className="text-xs font-semibold" style={{ color: '#16A34A' }}>Polite</p>
            </div>
            <p className="text-xs mb-3" style={{ color: '#9CA3AF' }}>Keeps it calm.</p>
            <p className="text-sm italic" style={{ color: '#374151', lineHeight: '1.6' }}>
              "I'm open to GAP, but I need the stand-alone price and time to compare it with my insurer/credit union. Please remove it for now."
            </p>
          </div>
          
          {/* Firm card */}
          <div className="p-4 rounded-xl" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#DC2626' }} />
              <p className="text-xs font-semibold" style={{ color: '#DC2626' }}>Firm</p>
            </div>
            <p className="text-xs mb-3" style={{ color: '#9CA3AF' }}>Sets a boundary.</p>
            <p className="text-sm italic" style={{ color: '#374151', lineHeight: '1.6' }}>
              "I already have GAP elsewhere. Please remove GAP — I'm not buying it at the dealership."
            </p>
          </div>
        </div>
      </div>
      
      {/* 9. ALSO KNOWN AS - PILLS */}
      {item.aliases && item.aliases.length > 0 && (
        <div className="mb-4">
          <h4 className="text-xs font-semibold uppercase tracking-wide mb-3" style={{ color: '#9CA3AF' }}>Also known as</h4>
          <div className="flex flex-wrap gap-2">
            {item.aliases.map((alias, i) => (
              <span key={i} className="px-3 py-1.5 text-xs rounded-full" style={{ backgroundColor: '#F3F4F6', color: '#6B7280' }}>
                {alias}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* =====================================================================
   BACKUP: ExtendedWarrantyDetailView_OLD
   This is the previous version preserved for quick revert if needed.
   Do NOT delete this block.
   ===================================================================== */
/*
const ExtendedWarrantyDetailView_OLD = ({ item, onBack }) => {
  const tagStyle = TAG_CONFIG[item.tag] || TAG_CONFIG['common'];
  const [expandedQ, setExpandedQ] = React.useState(new Set());
  
  const toggleQuestion = (idx) => {
    setExpandedQ(prev => {
      const newSet = new Set(prev);
      if (newSet.has(idx)) newSet.delete(idx);
      else newSet.add(idx);
      return newSet;
    });
  };
  
  const redFlags = [
    "They won't give you the full contract + exclusions + cancellation terms BEFORE you sign.",
    "They won't clearly name who backs it (administrator / obligor).",
    "Pre-authorization required but they won't explain the steps in writing.",
    "Reimbursement-only with vague timelines or unclear documentation rules.",
    "Strict shop restrictions that limit your repair options.",
    "Deductible rules are unclear (per visit vs per repair item).",
    "Vague maintenance-record requirements that could be used to deny claims."
  ];
  
  // ... (full component body preserved in backup - truncated for brevity)
  // Original had: green/pink decision guide, yellow deductible callout, blue finance office box
};
*/
/* =====================================================================
   END BACKUP
   ===================================================================== */

// Extended Warranty Detail View - Restyled to match GAP page (clean, premium, consistent)
const ExtendedWarrantyDetailView = ({ item, onBack }) => {
  const tagStyle = TAG_CONFIG[item.tag] || TAG_CONFIG['common'];
  const [expandedQ, setExpandedQ] = React.useState(new Set());
  const [expandedRedFlags, setExpandedRedFlags] = React.useState(new Set());
  const [expandedOtherOptions, setExpandedOtherOptions] = React.useState(new Set());
  const [expandedNegotiation, setExpandedNegotiation] = React.useState(new Set());
  const [expandedProviders, setExpandedProviders] = React.useState(new Set());
  const [considerExpanded, setConsiderExpanded] = React.useState(true);
  const [notNeedExpanded, setNotNeedExpanded] = React.useState(true);
  
  const toggleQuestion = (idx) => {
    setExpandedQ(prev => {
      const newSet = new Set(prev);
      if (newSet.has(idx)) newSet.delete(idx);
      else newSet.add(idx);
      return newSet;
    });
  };
  
  const toggleRedFlag = (idx) => {
    setExpandedRedFlags(prev => {
      const newSet = new Set(prev);
      if (newSet.has(idx)) newSet.delete(idx);
      else newSet.add(idx);
      return newSet;
    });
  };
  
  const toggleOtherOption = (idx) => {
    setExpandedOtherOptions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(idx)) newSet.delete(idx);
      else newSet.add(idx);
      return newSet;
    });
  };
  
  const toggleNegotiation = (idx) => {
    setExpandedNegotiation(prev => {
      const newSet = new Set(prev);
      if (newSet.has(idx)) newSet.delete(idx);
      else newSet.add(idx);
      return newSet;
    });
  };
  
  const toggleProvider = (idx) => {
    setExpandedProviders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(idx)) newSet.delete(idx);
      else newSet.add(idx);
      return newSet;
    });
  };
  
  // Negotiation steps data
  const negotiationSteps = [
    {
      title: "Ask for the total cash price + plan details",
      body: "Before you decide, get the total price, term/miles, deductible, coverage level (powertrain vs. named vs. exclusionary), the exclusions list, cancellation/refund terms, and the claims rules — in writing."
    },
    {
      title: "Get 2–4 competing quotes (remotely if possible)",
      body: "Text/email a few nearby dealers for the same coverage level, and compare at least one outside option. You're not committing — you're collecting numbers to create leverage."
    },
    {
      title: "Use the lowest quote to make them compete",
      body: "Show the best quote and ask them to match or beat it. If they won't move on price, ask for a better deductible ($0–$100), fewer restrictions, or stronger cancellation terms — in writing."
    },
    {
      title: "If it's financed, remember you pay interest on it",
      body: "If the VSC is rolled into the loan, you pay interest on it. Always ask what it costs as a standalone add-on (cash price) so you can compare fairly."
    },
    {
      title: "Confirm cancellation + where the refund goes",
      body: "Ask if refunds are pro-rated and if there are fees. If it's financed, refunds usually go to the loan balance (not cash back to you). Get that confirmed in writing."
    }
  ];
  
  // VSC providers data (only 3 providers - no CarShield)
  const vscProvidersData = [
    {
      name: "CARCHEX",
      whatItIs: "A marketplace that helps you compare multiple plan options and administrators.",
      goodFor: "Getting a baseline quote fast so you have leverage in the finance office.",
      watchFor: "Terms vary by plan/administrator — always read the actual contract and exclusions.",
      website: "carchex.com",
      websiteNote: "(type this into your browser)",
      isClickable: false
    },
    {
      name: "Endurance",
      whatItIs: "A direct provider with multiple coverage tiers (varies by vehicle).",
      goodFor: "Comparing plan levels if you're buying used or driving higher miles.",
      watchFor: "Confirm the claims steps (pre-authorization) and whether repairs are paid direct-to-shop or reimbursed.",
      websiteUrl: "https://www.endurancewarranty.com/",
      buttonLabel: "Visit Endurance",
      isClickable: true
    },
    {
      name: "American Dream Auto Protect",
      whatItIs: "A provider focused on simpler plan options and discounts (varies by vehicle).",
      goodFor: "Budget-minded shoppers who still want a written contract and clear rules.",
      watchFor: "Verify deductible rules (per visit vs per repair), shop restrictions, and cancellation terms in writing.",
      websiteUrl: "https://americandreamautoprotect.com/",
      buttonLabel: "Visit American Dream Auto Protect",
      isClickable: true
    }
  ];
  
  // Other options rows (3 numbered rows)
  const otherOptionsRows = [
    {
      title: "Dealer pricing is negotiable",
      body: "VSC prices are often negotiable. Ask for the total cash price (not just $/month), and ask if they can match an outside quote for similar coverage."
    },
    {
      title: "Compare apples-to-apples",
      body: "Same term length, mileage cap, deductible type (per visit vs per repair), and where repairs can be done."
    },
    {
      title: "Use outside quotes as leverage",
      body: "You can say: \"If I can get similar coverage for $X elsewhere, can you match it?\" If anything is unclear, decline today — you can usually decide later."
    }
  ];
  
  // Other options data
  const otherOptionsData = [
    {
      title: "Credit union or bank (often the simplest)",
      body: "Many credit unions and banks offer Vehicle Service Contracts to members or borrowers.\n\nIf you already finance through a credit union, ask what VSC options they offer for your exact vehicle and term.\n\nPros: pricing is often clearer than a dealer quote.\n\nWatch for: the same rules still apply — get the full contract, exclusions, deductible, and cancellation terms in writing."
    },
    {
      title: "Your auto insurer (mechanical breakdown coverage)",
      body: "Some insurers offer mechanical breakdown coverage (sometimes called MBI).\n\nPros: it can be billed monthly and is usually easier to cancel later if you don't need it anymore.\n\nWatch for: eligibility is often limited (newer cars / lower mileage) and coverage varies by policy — ask for the written terms."
    },
    {
      title: "Manufacturer-backed service plan (brand dealers)",
      body: "Some brands sell a manufacturer-backed service plan (even after you buy the car).\n\nPros: repairs may be simpler at brand dealers and can feel more predictable.\n\nWatch for: it can still be negotiable — and you should still read exclusions and claim rules."
    },
    {
      title: "Independent third-party VSC companies",
      body: "You can sometimes buy a VSC directly from an independent company (not the dealership).\n\nPros: you may find competitive pricing and more flexible repair options.\n\nWatch for: quality varies widely — confirm who the obligor/administrator is, where repairs can be done, and whether they pay the shop directly or reimburse you."
    },
    {
      title: "Alternative: build a repair fund",
      body: "Instead of a VSC, you can set aside the same amount you'd pay each month into a repair fund.\n\nPros: if you don't use it, you keep the money.\n\nWatch for: you need enough saved to cover a big repair if it happens early."
    }
  ];
  
  // Red flags with full body content
  const redFlagsData = [
    {
      title: "They won't give you the full contract + exclusions + cancellation terms BEFORE you sign.",
      body: "If you can't read it, you can't compare it.\n\nAsk for the full contract PDF (including exclusions and cancellation/refund terms) before you agree to anything.\n\nIf they won't provide it, decline today and decide later after you've read it."
    },
    {
      title: "They won't clearly name who backs it (administrator / obligor).",
      body: "The administrator runs the claims process day-to-day. The obligor is who is legally responsible to pay.\n\nIf they won't name both in writing, you don't know who you're relying on.\n\nAsk for the obligor and administrator names on the contract."
    },
    {
      title: "Pre-authorization required but they won't explain the steps in writing.",
      body: "Many plans require an authorization number before work starts — otherwise the claim can be denied.\n\nAsk what the shop must do before teardown and what number they call.\n\nIf the steps are vague, treat it as a major risk."
    },
    {
      title: "Reimbursement-only with vague timelines or unclear documentation rules.",
      body: "Reimbursement means you pay first and wait — which can be thousands out of pocket.\n\nAsk how long reimbursement typically takes and exactly what documents are required.\n\nIf they can't explain it clearly, assume delays or denials are likely."
    },
    {
      title: "Strict shop restrictions that limit your repair options.",
      body: "Some plans force you into a network or specific shops, which can be inconvenient or more expensive.\n\nAsk where repairs can be done (brand dealer vs any licensed shop) and get it in writing.\n\nIf they promise 'any shop' verbally but the contract restricts it, don't buy it."
    },
    {
      title: "Deductible rules are unclear (per visit vs per repair item).",
      body: "Per visit means you pay once for the appointment. Per repair item can stack multiple times in one visit.\n\nAsk which one it is and get the wording in writing.\n\nIf they dodge the question, assume the worst."
    },
    {
      title: "Vague maintenance-record requirements that could be used to deny claims.",
      body: "Many contracts require proof you followed the maintenance schedule.\n\nAsk what records you must keep and what happens if you're missing one receipt.\n\nIf they won't clarify the rules, claims can be denied later."
    }
  ];
  
  // Q&A data with plain-English body content
  const qaItems = [
    {
      q: "Is this manufacturer-backed, dealer-backed, or third-party? Who is the obligor and administrator?",
      body: "What it means: The administrator handles claims day-to-day. The obligor is who is legally responsible to pay.\n\nWhy it matters: If you don't know who backs it, you can't vet who you're relying on or who to contact later.\n\nGood answer: Both names are shown on the contract (with phone/address).\n\nRed flag: They only say 'it's through the dealership' or won't name the obligor in writing."
    },
    {
      q: "Is this exclusionary or named-component?",
      body: "What it means: Exclusionary covers most things except what's listed as excluded. Named-component covers only the parts that are specifically listed.\n\nWhy it matters: Named-component plans can surprise you when a part isn't on the list.\n\nGood answer: They show the exclusions list (or the full covered-parts list) in writing.\n\nRed flag: They call it 'bumper-to-bumper' but won't show exclusions."
    },
    {
      q: "Can I see the full contract now — including exclusions and the definition of breakdown?",
      body: "What it means: Coverage depends on definitions, exclusions, and claim rules — not sales talk.\n\nWhy it matters: 'Breakdown' may exclude wear/tear, seals/gaskets, diagnostic time, or other common items.\n\nGood answer: Full contract now (PDF/print), including definitions + exclusions.\n\nRed flag: 'You'll get it after you sign.'"
    },
    {
      q: "Is pre-authorization required before any work starts? What number does the shop call?",
      body: "What it means: Before repairs start, the shop may need approval from the VSC company.\n\nTypical process: The shop diagnoses the issue, calls the claims number, gets an authorization number, then starts work.\n\nGood answer: They can explain the steps and the claims number is on the contract.\n\nRed flag: Pre-auth is required but the steps are vague or not in writing."
    },
    {
      q: "Do you pay the shop directly, or do I pay first and get reimbursed?",
      body: "Option A (direct pay): After approval, the plan pays the shop and you pay your deductible and any non-covered items.\n\nOption B (reimbursement): You pay up front, then submit paperwork and wait for a check.\n\nWhy it matters: Reimbursement can be tough if cash is tight.\n\nRed flag: They can't explain timelines or required documents."
    },
    {
      q: "Where can repairs be done? Any network restrictions?",
      body: "Some plans allow any licensed shop. Others require network shops or the selling dealer.\n\nWhy it matters: Restrictions can limit where you can go — especially when traveling — or push you into pricier shops.\n\nGood answer: Repair location rules are clearly written in the contract.\n\nRed flag: They say 'any shop' verbally, but the contract limits it."
    },
    {
      q: "What is the deductible — and is it per visit or per repair item?",
      body: "Ask whether the deductible is per visit or per repair item — it changes your real cost.\n\nExample: $100 per visit = $100 once. $100 per repair item can stack in one appointment.\n\nGood answer: The deductible and how it's applied are written clearly.\n\nRed flag: The deductible is described differently in different places."
    },
    {
      q: "What maintenance records do I need to keep?",
      body: "Many contracts require you to follow the manufacturer maintenance schedule.\n\nKeep: oil change receipts, major service records, and mileage/date proof.\n\nWhy it matters: Missing records are a common reason claims get denied.\n\nRed flag: They can't explain what records you must keep."
    },
    {
      q: "How does cancellation work — and where does the refund go if this is financed?",
      body: "Most contracts are pro-rated by time and/or miles.\n\nIf it's financed, refunds often go to the lender (reduces loan balance, not your monthly payment).\n\nAsk: cancellation fees, refund formula, and where the refund goes — get it in writing.\n\nRed flag: No clear cancellation math or big fees not disclosed up front."
    }
  ];
  
  return (
    <div data-testid="item-detail-view" style={{ backgroundColor: '#FFFFFF' }}>
      {/* Back button */}
      <button onClick={onBack} className="flex items-center gap-1 text-sm font-medium mb-5 hover:underline" style={{ color: '#0284C7' }} data-testid="back-to-selected">
        <ChevronLeft className="w-4 h-4" />Back to selected items
      </button>
      
      {/* HEADER - matches GAP */}
      <div className="flex items-start justify-between mb-2">
        <div>
          <h3 className="text-xl font-bold mb-1" style={{ color: BRAND.navy }}>Extended warranty (Vehicle Service Contract)</h3>
          <p className="text-sm" style={{ color: '#6B7280', lineHeight: '1.6' }}>
            Optional repair coverage after the factory warranty ends. It can help with big unexpected repairs — but contracts vary a lot, and claim rules matter. Get the full contract details in writing before you decide.
          </p>
        </div>
        <span className="px-3 py-1.5 text-xs font-medium rounded-lg flex-shrink-0" style={{ backgroundColor: '#F3F4F6', color: '#6B7280', border: '1px solid #E5E7EB' }}>
          {tagStyle.label}
        </span>
      </div>
      
      {/* Divider - matches GAP */}
      <div style={{ height: '1px', backgroundColor: '#E5E7EB', margin: '20px 0' }} />
      
      {/* A) 5-MINUTE CHEAT SHEET - ALWAYS VISIBLE, GAP-style card with dividers */}
      <div className="mb-6 p-5 rounded-2xl" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <div className="flex items-center gap-2 mb-4">
          <ListChecks className="w-4 h-4" style={{ color: BRAND.navy }} />
          <h4 className="text-sm font-semibold" style={{ color: BRAND.navy }}>5-minute cheat sheet</h4>
        </div>
        <div className="space-y-0">
          {/* Step 1 */}
          <div className="flex items-start gap-3 py-3" style={{ borderBottom: '1px solid #F3F4F6' }}>
            <span className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-semibold" style={{ backgroundColor: '#F3F4F6', color: BRAND.navy }}>1</span>
            <div>
              <p className="text-sm font-medium mb-0.5" style={{ color: '#374151' }}>Ask for the cash total</p>
              <p className="text-xs" style={{ color: '#6B7280', lineHeight: '1.5' }}>If they quote monthly, ask for the total cost in dollars. If it's financed, you pay interest on it too.</p>
            </div>
          </div>
          {/* Step 2 */}
          <div className="flex items-start gap-3 py-3" style={{ borderBottom: '1px solid #F3F4F6' }}>
            <span className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-semibold" style={{ backgroundColor: '#F3F4F6', color: BRAND.navy }}>2</span>
            <div>
              <p className="text-sm font-medium mb-0.5" style={{ color: '#374151' }}>Pick a level that matches your risk</p>
              <p className="text-xs" style={{ color: '#6B7280', lineHeight: '1.5' }}>Powertrain = basics. Named-component = only listed parts. Exclusionary = covers most, but still has exclusions and claim rules.</p>
            </div>
          </div>
          {/* Step 3 */}
          <div className="flex items-start gap-3 py-3">
            <span className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-semibold" style={{ backgroundColor: '#F3F4F6', color: BRAND.navy }}>3</span>
            <div>
              <p className="text-sm font-medium mb-0.5" style={{ color: '#374151' }}>Use the 9 questions below</p>
              <p className="text-xs" style={{ color: '#6B7280', lineHeight: '1.5' }}>Get answers in writing. If they won't explain exclusions, claims steps, or refunds clearly — treat it as a red flag.</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* B) SHOULD I CONSIDER THIS? - Accordion cards matching "What to ask" style */}
      <div className="mb-6 p-5 rounded-2xl" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <div className="flex items-center gap-2 mb-4">
          <HelpCircle className="w-4 h-4" style={{ color: BRAND.navy }} />
          <h4 className="text-sm font-semibold" style={{ color: BRAND.navy }}>Should I consider this?</h4>
        </div>
        
        <div className="space-y-3">
          {/* First accordion - May want to consider */}
          <div className="rounded-xl overflow-hidden" style={{ border: '1px solid #E5E7EB' }}>
            <button
              onClick={() => setConsiderExpanded(!considerExpanded)}
              className="w-full flex items-center justify-between p-3 text-left transition-colors hover:bg-gray-50"
              style={{ backgroundColor: '#FFFFFF' }}
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <span className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: '#F3F4F6' }}>
                  <Check className="w-3 h-3" style={{ color: '#6B7280' }} />
                </span>
                <p className="text-xs font-medium uppercase tracking-wide" style={{ color: '#374151' }}>You may want to consider a VSC if…</p>
              </div>
              <ChevronDown 
                className="w-4 h-4 flex-shrink-0 ml-2 transition-transform" 
                style={{ color: '#9CA3AF', transform: considerExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }} 
              />
            </button>
            {considerExpanded && (
              <div className="px-4 pb-4" style={{ backgroundColor: '#F9FAFB' }}>
                <div className="space-y-0">
                  {["You're buying used and the factory warranty is expired (or almost expired).",
                    "A big repair bill would strain your savings.",
                    "You plan to keep the car for several years.",
                    "The vehicle is expensive to repair (high-tech, luxury, turbo, AWD, etc.).",
                    "You drive a lot of miles each year."
                  ].map((text, i) => (
                    <div key={i} className="flex items-start gap-2 py-2" style={{ borderBottom: i < 4 ? '1px solid #E5E7EB' : 'none' }}>
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#9CA3AF' }} />
                      <p className="text-sm" style={{ color: '#5C6370', lineHeight: '1.5' }}>{text}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* Second accordion - May not need */}
          <div className="rounded-xl overflow-hidden" style={{ border: '1px solid #E5E7EB' }}>
            <button
              onClick={() => setNotNeedExpanded(!notNeedExpanded)}
              className="w-full flex items-center justify-between p-3 text-left transition-colors hover:bg-gray-50"
              style={{ backgroundColor: '#FFFFFF' }}
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <span className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: '#F3F4F6' }}>
                  <X className="w-3 h-3" style={{ color: '#6B7280' }} />
                </span>
                <p className="text-xs font-medium uppercase tracking-wide" style={{ color: '#374151' }}>You may not need one if…</p>
              </div>
              <ChevronDown 
                className="w-4 h-4 flex-shrink-0 ml-2 transition-transform" 
                style={{ color: '#9CA3AF', transform: notNeedExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }} 
              />
            </button>
            {notNeedExpanded && (
              <div className="px-4 pb-4" style={{ backgroundColor: '#F9FAFB' }}>
                <div className="space-y-0">
                  {["You're buying new with strong factory coverage.",
                    "You expect to sell or trade in 2–3 years.",
                    "You already have a repair fund (or could build one).",
                    "You're buying a very reliable model and repairs wouldn't be a financial hit."
                  ].map((text, i) => (
                    <div key={i} className="flex items-start gap-2 py-2" style={{ borderBottom: i < 3 ? '1px solid #E5E7EB' : 'none' }}>
                      <X className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#9CA3AF' }} />
                      <p className="text-sm" style={{ color: '#5C6370', lineHeight: '1.5' }}>{text}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        
        <p className="text-xs mt-4 text-center" style={{ color: '#9CA3AF' }}>
          Tip: A VSC is optional. If the terms are unclear today, you can usually decline and decide later.
        </p>
        <p className="text-xs mt-2 text-center" style={{ color: '#9CA3AF' }}>
          Extra tip: Mileage + repair risk matter. A high-mileage or high-tech vehicle is usually higher risk than a newer, low-mileage one.
        </p>
      </div>
      
      {/* C) TYPICAL PRICE - matches GAP */}
      <div className="mb-6 p-5 rounded-2xl" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <div className="flex items-center gap-2 mb-4">
          <DollarSign className="w-4 h-4" style={{ color: BRAND.navy }} />
          <h4 className="text-sm font-semibold" style={{ color: BRAND.navy }}>Typical price</h4>
        </div>
        
        <div className="space-y-5">
          {/* Third-party row */}
          <div className="pb-4" style={{ borderBottom: '1px solid #F3F4F6' }}>
            <p className="text-xs font-medium uppercase tracking-wide mb-1" style={{ color: '#9CA3AF' }}>Third-party / credit union (often lower)</p>
            <p className="text-sm font-medium mb-2" style={{ color: '#374151' }}>$1,500–$3,000 total for multi-year plans</p>
            <p className="text-xs" style={{ color: '#9CA3AF', lineHeight: '1.5' }}>
              Shop around — prices vary widely by provider and coverage level.
            </p>
          </div>
          
          {/* Dealer row */}
          <div>
            <p className="text-xs font-medium uppercase tracking-wide mb-1" style={{ color: '#9CA3AF' }}>Dealer (often higher)</p>
            <p className="text-sm font-medium mb-2" style={{ color: '#374151' }}>$2,000–$5,000+ total</p>
            <p className="text-xs" style={{ color: '#9CA3AF', lineHeight: '1.5' }}>
              Dealer markup can be significant — first price is rarely the best.
            </p>
          </div>
        </div>
        
        {/* Price varies note - subtle like GAP example callout */}
        <div className="mt-4 p-3 rounded-lg" style={{ backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB' }}>
          <p className="text-xs" style={{ color: '#6B7280', lineHeight: '1.6' }}>
            <span className="font-semibold">Price varies by:</span> Coverage level (powertrain vs exclusionary), term length, vehicle type (luxury/European costs more), and mileage.
          </p>
        </div>
        
        <div className="mt-4 pt-3 space-y-1" style={{ borderTop: '1px solid #F3F4F6' }}>
          <p className="text-xs" style={{ color: '#6B7280', lineHeight: '1.5' }}>Some providers quote a TOTAL (example: $2,000–$4,500). Some dealers quote MONTHLY (example: $85–$210/mo) which can hide the total.</p>
          <p className="text-xs mt-2 font-medium" style={{ color: '#374151' }}>Always ask for the total cash price, and ask if it's being financed (financing adds interest).</p>
        </div>
      </div>
      
      {/* OTHER OPTIONS (outside the dealership) */}
      <div className="mb-6 p-5 rounded-2xl" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <div className="flex items-center gap-2 mb-2">
          <HelpCircle className="w-4 h-4" style={{ color: BRAND.navy }} />
          <h4 className="text-sm font-semibold" style={{ color: BRAND.navy }}>Other options (outside the dealership)</h4>
        </div>
        <p className="text-xs mb-4" style={{ color: '#6B7280', lineHeight: '1.5' }}>
          You can usually buy a Vehicle Service Contract (VSC) from places other than the dealer. Compare total cost and the contract terms.
        </p>
        <p className="text-xs font-medium mb-3" style={{ color: '#9CA3AF' }}>Tap for details</p>
        <div className="space-y-2">
          {otherOptionsData.map((option, i) => (
            <div key={i} className="rounded-xl overflow-hidden" style={{ border: '1px solid #E5E7EB' }}>
              <button
                onClick={() => toggleOtherOption(i)}
                className="w-full flex items-center justify-between p-3 text-left transition-colors hover:bg-gray-50"
                style={{ backgroundColor: '#FFFFFF' }}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-semibold" style={{ backgroundColor: '#F3F4F6', color: BRAND.navy }}>{i + 1}</span>
                  <p className="text-sm" style={{ color: '#374151', lineHeight: '1.4' }}>{option.title}</p>
                </div>
                <ChevronDown 
                  className="w-4 h-4 flex-shrink-0 ml-2 transition-transform" 
                  style={{ color: '#9CA3AF', transform: expandedOtherOptions.has(i) ? 'rotate(180deg)' : 'rotate(0deg)' }} 
                />
              </button>
              {expandedOtherOptions.has(i) && (
                <div className="px-4 pb-4" style={{ backgroundColor: '#F9FAFB' }}>
                  {option.body.split('\n\n').map((paragraph, j) => (
                    <p key={j} className="text-sm mb-2 last:mb-0" style={{ color: '#5C6370', lineHeight: '1.6' }}>{paragraph}</p>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      
      {/* DEALER PRICING IS NEGOTIABLE */}
      <div className="mb-6 p-5 rounded-2xl" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <div className="flex items-center gap-2 mb-2">
          <DollarSign className="w-4 h-4" style={{ color: BRAND.navy }} />
          <h4 className="text-sm font-semibold" style={{ color: BRAND.navy }}>Dealer pricing is negotiable</h4>
        </div>
        <p className="text-xs mb-4" style={{ color: '#6B7280', lineHeight: '1.5' }}>
          VSC pricing usually isn't fixed. These are high-margin add-ons, so the first number you hear is often negotiable. Your leverage comes from getting competing quotes and comparing the total cash price (not just "$X per month").
        </p>
        <div className="space-y-2">
          {negotiationSteps.map((step, i) => (
            <div key={i} className="rounded-xl overflow-hidden" style={{ border: '1px solid #E5E7EB' }}>
              <button
                onClick={() => toggleNegotiation(i)}
                className="w-full flex items-center justify-between p-3 text-left transition-colors hover:bg-gray-50"
                style={{ backgroundColor: '#FFFFFF' }}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-semibold" style={{ backgroundColor: '#F3F4F6', color: BRAND.navy }}>{i + 1}</span>
                  <p className="text-sm" style={{ color: '#374151', lineHeight: '1.4' }}>{step.title}</p>
                </div>
                <ChevronDown 
                  className="w-4 h-4 flex-shrink-0 ml-2 transition-transform" 
                  style={{ color: '#9CA3AF', transform: expandedNegotiation.has(i) ? 'rotate(180deg)' : 'rotate(0deg)' }} 
                />
              </button>
              {expandedNegotiation.has(i) && (
                <div className="px-4 pb-4" style={{ backgroundColor: '#F9FAFB' }}>
                  <p className="text-sm" style={{ color: '#5C6370', lineHeight: '1.6' }}>{step.body}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      
      {/* D) DEDUCTIBLE QUICK EXAMPLE - WHITE card, NO yellow fill */}
      <div className="mb-6 p-5 rounded-2xl" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <div className="flex items-center gap-2 mb-4">
          <Info className="w-4 h-4" style={{ color: BRAND.navy }} />
          <h4 className="text-sm font-semibold" style={{ color: BRAND.navy }}>Deductible (quick example)</h4>
        </div>
        <div className="space-y-0">
          <div className="flex items-start gap-4 py-3" style={{ borderBottom: '1px solid #F3F4F6' }}>
            <span className="text-sm font-medium flex-shrink-0" style={{ color: '#374151', minWidth: '100px' }}>Per visit:</span>
            <p className="text-sm" style={{ color: '#5C6370', lineHeight: '1.5' }}>You pay $100 once for that repair visit — even if multiple covered parts are fixed.</p>
          </div>
          <div className="flex items-start gap-4 py-3" style={{ borderBottom: '1px solid #F3F4F6' }}>
            <span className="text-sm font-medium flex-shrink-0" style={{ color: '#374151', minWidth: '100px' }}>Per repair item:</span>
            <p className="text-sm" style={{ color: '#5C6370', lineHeight: '1.5' }}>You might pay $100 more than once in the same visit (ex: $100 for A/C + $100 for alternator).</p>
          </div>
          <p className="text-sm font-medium pt-3" style={{ color: '#374151' }}>Ask which one it is — it changes the real cost.</p>
        </div>
      </div>
      
      {/* E) CANCELLATION & REFUNDS - matches GAP style, gray check icons */}
      <div className="mb-6 p-5 rounded-2xl" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <div className="flex items-center gap-2 mb-3">
          <Info className="w-4 h-4" style={{ color: BRAND.navy }} />
          <h4 className="text-sm font-semibold" style={{ color: BRAND.navy }}>Cancellation & refunds (what happens)</h4>
        </div>
        <div className="space-y-0">
          <div className="flex items-start gap-3 py-2" style={{ borderBottom: '1px solid #F3F4F6' }}>
            <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#9CA3AF' }} />
            <p className="text-sm" style={{ color: '#5C6370', lineHeight: '1.5' }}>Most contracts are pro-rated — your refund depends on time and/or miles used.</p>
          </div>
          <div className="flex items-start gap-3 py-2" style={{ borderBottom: '1px solid #F3F4F6' }}>
            <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#9CA3AF' }} />
            <p className="text-sm" style={{ color: '#5C6370', lineHeight: '1.5' }}>Some charge a cancellation fee (ask the exact amount).</p>
          </div>
          <div className="flex items-start gap-3 py-2" style={{ borderBottom: '1px solid #F3F4F6' }}>
            <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#9CA3AF' }} />
            <p className="text-sm" style={{ color: '#5C6370', lineHeight: '1.5' }}>If it's financed, refunds often go to the lender (reduces your loan balance, not your monthly payment).</p>
          </div>
          <div className="flex items-start gap-3 py-2">
            <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#9CA3AF' }} />
            <p className="text-sm font-medium" style={{ color: '#374151', lineHeight: '1.5' }}>Get the cancellation terms in writing before you sign.</p>
          </div>
        </div>
      </div>
      
      {/* OTHER OPTIONS OUTSIDE THIS DEALERSHIP - Combined section */}
      <div className="mb-6 p-5 rounded-2xl" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <div className="flex items-center gap-2 mb-2">
          <HelpCircle className="w-4 h-4" style={{ color: BRAND.navy }} />
          <h4 className="text-sm font-semibold" style={{ color: BRAND.navy }}>Other options outside this dealership</h4>
        </div>
        <p className="text-sm mb-4" style={{ color: '#6B7280', lineHeight: '1.5' }}>
          You usually have options beyond the dealership you're sitting in. We don't partner with any of these companies, and we don't earn commissions — this is just a starting point so you can compare coverage and price.
        </p>
        
        {/* 3 numbered rows */}
        <div className="space-y-0 mb-5">
          {otherOptionsRows.map((row, i) => (
            <div key={i} className="flex items-start gap-3 py-3" style={{ borderBottom: i < otherOptionsRows.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
              <span className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-semibold" style={{ backgroundColor: '#F3F4F6', color: BRAND.navy }}>{i + 1}</span>
              <div>
                <p className="text-sm font-medium mb-0.5" style={{ color: '#374151' }}>{row.title}</p>
                <p className="text-xs" style={{ color: '#6B7280', lineHeight: '1.5' }}>{row.body}</p>
              </div>
            </div>
          ))}
        </div>
        
        {/* Nested accordion for providers */}
        <div className="pt-4" style={{ borderTop: '1px solid #E5E7EB' }}>
          <p className="text-xs font-medium mb-3" style={{ color: '#9CA3AF' }}>3 reputable starting points (tap for details)</p>
          <div className="space-y-2">
            {vscProvidersData.map((provider, i) => (
              <div key={i} className="rounded-xl overflow-hidden" style={{ border: '1px solid #E5E7EB' }}>
                <button
                  onClick={() => toggleProvider(i)}
                  className="w-full flex items-center justify-between p-3 text-left transition-colors hover:bg-gray-50"
                  style={{ backgroundColor: '#FFFFFF' }}
                >
                  <p className="text-sm font-medium" style={{ color: '#374151' }}>{provider.name}</p>
                  <ChevronDown 
                    className="w-4 h-4 flex-shrink-0 ml-2 transition-transform" 
                    style={{ color: '#9CA3AF', transform: expandedProviders.has(i) ? 'rotate(180deg)' : 'rotate(0deg)' }} 
                  />
                </button>
                {expandedProviders.has(i) && (
                  <div className="px-4 pb-4" style={{ backgroundColor: '#F9FAFB' }}>
                    <div className="py-2" style={{ borderBottom: '1px solid #E5E7EB' }}>
                      <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: BRAND.navy }}>What it is</p>
                      <p className="text-sm" style={{ color: '#5C6370', lineHeight: '1.5' }}>{provider.whatItIs}</p>
                    </div>
                    <div className="py-2" style={{ borderBottom: '1px solid #E5E7EB' }}>
                      <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: BRAND.navy }}>Good for</p>
                      <p className="text-sm" style={{ color: '#5C6370', lineHeight: '1.5' }}>{provider.goodFor}</p>
                    </div>
                    <div className="py-2" style={{ borderBottom: '1px solid #E5E7EB' }}>
                      <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: '#DC2626' }}>Watch for</p>
                      <p className="text-sm" style={{ color: '#5C6370', lineHeight: '1.5' }}>{provider.watchFor}</p>
                    </div>
                    <div className="py-2">
                      <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: BRAND.navy }}>Website</p>
                      {provider.isClickable ? (
                        <a 
                          href={provider.websiteUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors hover:opacity-80"
                          style={{ backgroundColor: BRAND.navy, color: '#FFFFFF' }}
                        >
                          {provider.buttonLabel}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      ) : (
                        <p className="text-sm" style={{ color: '#5C6370', lineHeight: '1.5' }}>
                          {provider.website} <span style={{ color: '#9CA3AF' }}>{provider.websiteNote}</span>
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* F) RED FLAGS - Accordion style matching "What to ask" */}
      <div className="mb-6 p-5 rounded-2xl" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle className="w-4 h-4" style={{ color: '#DC2626' }} />
          <h4 className="text-sm font-semibold" style={{ color: '#DC2626' }}>Red flags</h4>
          <span className="text-xs" style={{ color: '#9CA3AF' }}>Tap for details</span>
        </div>
        <p className="text-xs mb-2" style={{ color: '#6B7280', lineHeight: '1.5' }}>
          Reality check: most frustration isn't the idea of coverage — it's denied claims or delays.
        </p>
        <p className="text-xs mb-4" style={{ color: '#6B7280', lineHeight: '1.5' }}>
          Even exclusionary plans can deny claims — exclusions and the claims process matter as much as the price.
        </p>
        <div className="space-y-2">
          {redFlagsData.map((flag, i) => (
            <div key={i} className="rounded-xl overflow-hidden" style={{ border: '1px solid #E5E7EB' }}>
              <button
                onClick={() => toggleRedFlag(i)}
                className="w-full flex items-center justify-between p-3 text-left transition-colors hover:bg-gray-50 min-h-[56px]"
                style={{ backgroundColor: '#FFFFFF' }}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" style={{ color: '#F87171' }} />
                  <p className="text-sm truncate pr-3" style={{ color: '#374151', lineHeight: '1.4' }}>{flag.title}</p>
                </div>
                <ChevronDown 
                  className="w-4 h-4 flex-shrink-0 ml-2 transition-transform" 
                  style={{ color: '#9CA3AF', transform: expandedRedFlags.has(i) ? 'rotate(180deg)' : 'rotate(0deg)' }} 
                />
              </button>
              {expandedRedFlags.has(i) && (
                <div className="px-4 pb-4" style={{ backgroundColor: '#F9FAFB' }}>
                  <p className="text-sm font-medium mb-2" style={{ color: '#374151', lineHeight: '1.5' }}>{flag.title}</p>
                  {flag.body.split('\n\n').map((paragraph, j) => (
                    <p key={j} className="text-sm mb-2 last:mb-0" style={{ color: '#5C6370', lineHeight: '1.6' }}>{paragraph}</p>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
        {/* Denial example - clean info card */}
        <div className="mt-4 p-4 rounded-xl" style={{ backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB' }}>
          <p className="text-xs font-semibold mb-1" style={{ color: '#DC2626' }}>Real-world denial example</p>
          <p className="text-sm" style={{ color: '#5C6370', lineHeight: '1.5' }}>
            A/C repair denied because the contract excluded a specific part (like seals/gaskets) and required pre-authorization before teardown.
          </p>
        </div>
      </div>
      
      {/* G) WHAT TO ASK - accordion matches GAP row styling */}
      <div className="mb-6 p-5 rounded-2xl" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <div className="flex items-center gap-2 mb-4">
          <HelpCircle className="w-4 h-4" style={{ color: BRAND.navy }} />
          <h4 className="text-sm font-semibold" style={{ color: BRAND.navy }}>What to ask (tap for details)</h4>
        </div>
        <div className="space-y-2">
          {qaItems.map((qa, i) => (
            <div key={i} className="rounded-xl overflow-hidden" style={{ border: '1px solid #E5E7EB' }}>
              <button
                onClick={() => toggleQuestion(i)}
                className="w-full flex items-center justify-between p-3 text-left transition-colors hover:bg-gray-50"
                style={{ backgroundColor: '#FFFFFF' }}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-semibold" style={{ backgroundColor: '#F3F4F6', color: BRAND.navy }}>{i + 1}</span>
                  <p className="text-sm" style={{ color: '#374151', lineHeight: '1.4' }}>{qa.q}</p>
                </div>
                <ChevronDown 
                  className="w-4 h-4 flex-shrink-0 ml-2 transition-transform" 
                  style={{ color: '#9CA3AF', transform: expandedQ.has(i) ? 'rotate(180deg)' : 'rotate(0deg)' }} 
                />
              </button>
              {expandedQ.has(i) && (
                <div className="px-4 pb-4" style={{ backgroundColor: '#F9FAFB' }}>
                  {qa.body.split('\n\n').map((paragraph, j) => (
                    <p key={j} className="text-sm mb-2 last:mb-0" style={{ color: '#5C6370', lineHeight: '1.6' }}>{paragraph}</p>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      
      {/* H) IF YOU'RE IN THE FINANCE OFFICE RIGHT NOW - subtle neutral tint like GAP example */}
      <div className="mb-6 p-5 rounded-2xl" style={{ backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <div className="flex items-center gap-2 mb-3">
          <Clock className="w-4 h-4" style={{ color: BRAND.navy }} />
          <h4 className="text-sm font-semibold" style={{ color: BRAND.navy }}>If you're in the finance office right now</h4>
        </div>
        <div className="space-y-0">
          <div className="flex items-start gap-3 py-2" style={{ borderBottom: '1px solid #E5E7EB' }}>
            <span className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-semibold" style={{ backgroundColor: '#FFFFFF', color: BRAND.navy, border: '1px solid #E5E7EB' }}>1</span>
            <p className="text-sm" style={{ color: '#374151', lineHeight: '1.5' }}>Ask for the total cash price and the full contract before you sign.</p>
          </div>
          <div className="flex items-start gap-3 py-2" style={{ borderBottom: '1px solid #E5E7EB' }}>
            <span className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-semibold" style={{ backgroundColor: '#FFFFFF', color: BRAND.navy, border: '1px solid #E5E7EB' }}>2</span>
            <p className="text-sm" style={{ color: '#374151', lineHeight: '1.5' }}>Use the questions above and get answers in writing.</p>
          </div>
          <div className="flex items-start gap-3 py-2">
            <span className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-semibold" style={{ backgroundColor: '#FFFFFF', color: BRAND.navy, border: '1px solid #E5E7EB' }}>3</span>
            <p className="text-sm font-medium" style={{ color: '#374151', lineHeight: '1.5' }}>If anything is unclear, decline today — you can decide later when you've had time to read.</p>
          </div>
        </div>
      </div>
      
      {/* I) WHAT TO SAY - matches GAP exactly (white cards, subtle dots) */}
      <div className="mb-6">
        <h4 className="text-sm font-semibold mb-4" style={{ color: BRAND.navy }}>What to say</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Polite card */}
          <div className="p-4 rounded-xl" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#16A34A' }} />
              <p className="text-xs font-semibold" style={{ color: '#16A34A' }}>Polite</p>
            </div>
            <p className="text-xs mb-3" style={{ color: '#9CA3AF' }}>Keeps it calm.</p>
            <p className="text-sm italic" style={{ color: '#374151', lineHeight: '1.6' }}>
              "I'm open to coverage, but I need the full contract and exclusions in writing. I'll review it before deciding."
            </p>
          </div>
          
          {/* Firm card */}
          <div className="p-4 rounded-xl" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#DC2626' }} />
              <p className="text-xs font-semibold" style={{ color: '#DC2626' }}>Firm</p>
            </div>
            <p className="text-xs mb-3" style={{ color: '#9CA3AF' }}>Sets a boundary.</p>
            <p className="text-sm italic" style={{ color: '#374151', lineHeight: '1.6' }}>
              "No service contract today. Please remove it from the deal."
            </p>
          </div>
        </div>
      </div>
      
      {/* ALSO KNOWN AS - matches GAP */}
      <div className="mb-4">
        <h4 className="text-xs font-semibold uppercase tracking-wide mb-3" style={{ color: '#9CA3AF' }}>Also known as</h4>
        <div className="flex flex-wrap gap-2">
          {['Extended service plan', 'Vehicle Service Contract (VSC)', 'Service contract', 'Protection plan', 'Mechanical breakdown coverage'].map((alias, i) => (
            <span key={i} className="px-3 py-1.5 text-xs rounded-full" style={{ backgroundColor: '#F3F4F6', color: '#6B7280' }}>
              {alias}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};
const ItemDetailView = ({ item, onBack }) => {
  const tagStyle = TAG_CONFIG[item.tag] || TAG_CONFIG['common'];
  const d = item.detail || {};
  const hasEnhancedPricing = d.typicalPrice && d.typicalPrice.byPlanLevelDealer;
  const isGAP = item.key === 'gap_insurance';
  const isExtendedWarranty = item.key === 'extended_warranty';
  
  // Use premium GAP view for GAP insurance
  if (isGAP) {
    return <GAPDetailView item={item} onBack={onBack} />;
  }
  
  // Use premium Extended Warranty view for extended warranty
  if (isExtendedWarranty) {
    return <ExtendedWarrantyDetailView item={item} onBack={onBack} />;
  }
  
  return (
    <div data-testid="item-detail-view" style={{ backgroundColor: '#FFFFFF' }}>
      <button onClick={onBack} className="flex items-center gap-1 text-sm font-medium mb-4 hover:underline" style={{ color: '#0284C7' }} data-testid="back-to-selected">
        <ChevronLeft className="w-4 h-4" />Back to selected items
      </button>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <h3 className="text-lg font-bold" style={{ color: BRAND.navy }}>{item.title}</h3>
        <span className="px-2 py-0.5 text-xs font-medium rounded-full" style={{ backgroundColor: tagStyle.bg, color: tagStyle.color }}>{tagStyle.label}</span>
      </div>
      <p className="text-sm mb-5" style={{ color: '#6B7280' }}>{item.subtitle}</p>
      
      {/* Typical Price - Enhanced format */}
      {hasEnhancedPricing ? (
        <div className="p-4 rounded-xl mb-5" style={{ backgroundColor: '#F0F9FF', border: '1px solid #E5E7EB' }}>
          <div className="flex items-start gap-2 mb-3">
            <DollarSign className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#0369A1' }} />
            <p className="text-xs font-semibold" style={{ color: '#0369A1' }}>Typical price</p>
          </div>
          <p className="text-xs font-semibold mb-2" style={{ color: '#0C4A6E' }}>By coverage level (dealer pricing):</p>
          <div className="space-y-2 mb-3">
            {d.typicalPrice.byPlanLevelDealer.map((level, i) => (
              <div key={i} className="p-3 rounded-lg" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB' }}>
                <p className="text-xs font-medium" style={{ color: '#0369A1' }}>{level.coverageLevel}: {level.dealerRange}</p>
                <p className="text-xs" style={{ color: '#6B7280', lineHeight: '1.5' }}>{level.notes}</p>
              </div>
            ))}
          </div>
          {d.typicalPrice.example7yr100kExclusionary && (
            <>
              <p className="text-xs font-semibold mb-2" style={{ color: '#0C4A6E' }}>7yr/100k exclusionary example by vehicle:</p>
              <div className="space-y-2 mb-3">
                {d.typicalPrice.example7yr100kExclusionary.map((ex, i) => (
                  <div key={i} className="p-3 rounded-lg" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB' }}>
                    <p className="text-xs font-medium" style={{ color: '#0369A1' }}>{ex.vehicleClass}</p>
                    <p className="text-xs" style={{ color: '#6B7280' }}>Dealer: {ex.dealerRange} | Third-party/CU: {ex.thirdPartyOrCURange}</p>
                    <p className="text-xs" style={{ color: '#9CA3AF' }}>{ex.notes}</p>
                  </div>
                ))}
              </div>
            </>
          )}
          {d.typicalPrice.deductibleTypical && (
            <p className="text-xs mb-2" style={{ color: '#0C4A6E' }}><strong>Deductible:</strong> {d.typicalPrice.deductibleTypical}</p>
          )}
          {d.typicalPrice.pricingNotes && d.typicalPrice.pricingNotes.length > 0 && (
            <ul className="text-xs space-y-1" style={{ color: '#6B7280' }}>
              {d.typicalPrice.pricingNotes.map((note, i) => <li key={i}>• {note}</li>)}
            </ul>
          )}
        </div>
      ) : (
        <div className="p-4 rounded-xl mb-5" style={{ backgroundColor: '#F0F9FF', border: '1px solid #E5E7EB' }}>
          <div className="flex items-start gap-2">
            <DollarSign className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#0369A1' }} />
            <div>
              <p className="text-xs font-semibold mb-1" style={{ color: '#0369A1' }}>Typical price</p>
              <p className="text-sm" style={{ color: '#0C4A6E', lineHeight: '1.6' }}>
                <strong>Consumer/aftermarket:</strong> {d.typical_consumer || 'Varies'}<br/>
                <strong>Dealer:</strong> {d.typical_dealer || 'Varies'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Why it matters */}
      <div className="mb-5">
        <h4 className="text-sm font-semibold mb-2 flex items-center gap-2" style={{ color: BRAND.navy }}>
          <AlertTriangle className="w-4 h-4" style={{ color: '#D97706' }} />Why it matters
        </h4>
        <p className="text-sm" style={{ color: '#5C6370', lineHeight: '1.6' }}>{d.why || 'Review this item carefully and ask for an itemized breakdown.'}</p>
      </div>

      {/* How it's sold */}
      {(d.sold || d.howItsSold) && (
        <div className="mb-5">
          <h4 className="text-sm font-semibold mb-2 flex items-center gap-2" style={{ color: BRAND.navy }}>
            <Info className="w-4 h-4" style={{ color: '#6B7280' }} />How it is sold
          </h4>
          {Array.isArray(d.howItsSold) ? (
            <div className="space-y-2">
              {d.howItsSold.map((line, i) => (
                <div key={i} className="pl-3" style={{ borderLeft: '3px solid #D1D5DB' }}>
                  <p className="text-sm italic" style={{ color: '#5C6370', lineHeight: '1.5' }}>{line}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="pl-3" style={{ borderLeft: '3px solid #D1D5DB' }}>
              <p className="text-sm italic" style={{ color: '#5C6370', lineHeight: '1.5' }}>{d.sold}</p>
            </div>
          )}
        </div>
      )}

      {/* Red flags */}
      {d.redFlags && d.redFlags.length > 0 && (
        <div className="mb-5">
          <h4 className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: '#DC2626' }}>
            <AlertTriangle className="w-4 h-4" style={{ color: '#DC2626' }} />Red flags
          </h4>
          <ul className="text-sm space-y-1" style={{ color: '#5C6370' }}>
            {d.redFlags.map((flag, i) => <li key={i}>• {flag}</li>)}
          </ul>
        </div>
      )}

      {/* What to ask */}
      {d.whatToAsk && d.whatToAsk.length > 0 && (
        <div className="mb-5">
          <h4 className="text-sm font-semibold mb-2 flex items-center gap-2" style={{ color: BRAND.navy }}>
            <HelpCircle className="w-4 h-4" style={{ color: '#0284C7' }} />What to ask
          </h4>
          <ul className="text-sm space-y-1" style={{ color: '#5C6370' }}>
            {d.whatToAsk.map((q, i) => <li key={i}>• {q}</li>)}
          </ul>
        </div>
      )}

      {/* What to say */}
      <div className="mb-5">
        <h4 className="text-sm font-semibold mb-3" style={{ color: BRAND.navy }}>What to say</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="p-4 rounded-xl" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB' }}>
            <p className="text-xs font-semibold mb-2" style={{ color: '#16A34A' }}>Polite</p>
            <p className="text-sm italic" style={{ color: '#374151', lineHeight: '1.5' }}>{d.say_polite || '"Can you help me understand what this covers?"'}</p>
          </div>
          <div className="p-4 rounded-xl" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB' }}>
            <p className="text-xs font-semibold mb-2" style={{ color: '#DC2626' }}>Firm</p>
            <p className="text-sm italic" style={{ color: '#374151', lineHeight: '1.5' }}>{d.say_firm || '"Please remove this from the deal."'}</p>
          </div>
        </div>
      </div>

      {/* Edge cases */}
      {d.edgeCases && d.edgeCases.length > 0 && (
        <div className="mb-5">
          <h4 className="text-sm font-semibold mb-2" style={{ color: BRAND.navy }}>Edge cases</h4>
          <ul className="text-sm space-y-1" style={{ color: '#5C6370' }}>
            {d.edgeCases.map((edge, i) => <li key={i}>• {edge}</li>)}
          </ul>
        </div>
      )}

      {/* Also known as */}
      {item.aliases && item.aliases.length > 0 && (
        <div className="mb-4">
          <h4 className="text-xs font-semibold mb-1" style={{ color: '#9CA3AF' }}>Also known as</h4>
          <p className="text-xs" style={{ color: '#9CA3AF' }}>{item.aliases.join(', ')}</p>
        </div>
      )}
    </div>
  );
};

// Section Header with helper text lines
const SectionHeader = ({ label, helperLines }) => (
  <div className="flex flex-col items-center py-4">
    <div className="flex items-center w-full gap-3 mb-3">
      <div className="flex-1 h-px" style={{ backgroundColor: '#E5E7EB' }} />
      <span className="text-xs font-semibold uppercase tracking-wide px-3 py-1 rounded-full" style={{ backgroundColor: '#FFFFFF', color: BRAND.navy, border: `1px solid ${BRAND.navy}` }}>{label}</span>
      <div className="flex-1 h-px" style={{ backgroundColor: '#E5E7EB' }} />
    </div>
    {helperLines && helperLines.map((line, i) => (
      <p key={i} className="text-center" style={{ color: '#6B7280', maxWidth: '380px', fontSize: '13px', lineHeight: '1.6', marginBottom: i < helperLines.length - 1 ? '8px' : '0' }}>{line}</p>
    ))}
  </div>
);

const ScanIntro = () => {
  const navigate = useNavigate();
  const reviewSectionRef = useRef(null);
  const bucketRefs = useRef({});
  
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [viewingItem, setViewingItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedBuckets, setExpandedBuckets] = useState(new Set());
  const [showItemizedTooltip, setShowItemizedTooltip] = useState(false);

  const scrollToReview = () => {
    reviewSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    if (reviewSectionRef.current) {
      reviewSectionRef.current.style.outline = '2px solid #0284C7';
      reviewSectionRef.current.style.outlineOffset = '4px';
      setTimeout(() => { if (reviewSectionRef.current) reviewSectionRef.current.style.outline = 'none'; }, 1500);
    }
  };

  const toggleItem = (key) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      newSet.has(key) ? newSet.delete(key) : newSet.add(key);
      return newSet;
    });
  };

  const removeItem = (key) => {
    setSelectedItems(prev => { const newSet = new Set(prev); newSet.delete(key); return newSet; });
    if (viewingItem?.key === key) setViewingItem(null);
  };

  const handleBucketClick = (bucketId) => {
    setExpandedBuckets(prev => {
      const newSet = new Set(prev);
      if (newSet.has(bucketId)) {
        newSet.delete(bucketId);
      } else {
        newSet.add(bucketId);
        setTimeout(() => bucketRefs.current[bucketId]?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      }
      return newSet;
    });
  };

  const filteredItems = searchItems(searchQuery, ITEMS);
  const isSearching = searchQuery.trim().length > 0;
  const bucketsWithMatches = isSearching ? BUCKETS.filter(b => filteredItems.some(i => i.bucket === b.id)) : BUCKETS;

  const selectedItemsArray = ITEMS.filter(i => selectedItems.has(i.key));
  const selectedCount = selectedItems.size;

  const itemizedBuckets = BUCKETS.filter(b => b.section === 'itemized');
  const addonBuckets = BUCKETS.filter(b => b.section === 'addons');

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FFFFFF' }}>
      {/* Header with brand wordmark */}
      <div style={{ backgroundColor: '#FFFFFF', borderBottom: '1px solid #E5E7EB' }}>
        <div className="max-w-screen-sm mx-auto px-5 py-4">
          <div className="flex items-center justify-between">
            <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm font-medium" style={{ color: '#6B7280' }} data-testid="back-btn">
              <ChevronLeft className="w-4 h-4" /><span>Back</span>
            </button>
            <span style={{ fontWeight: 700, fontSize: '14px', letterSpacing: '0.2px' }}>
              <span style={{ color: BRAND.navy }}>VINdicated</span><span style={{ color: BRAND.red }}>.ai</span>
            </span>
          </div>
        </div>
      </div>

      <div className="max-w-screen-sm mx-auto px-5 pb-32">
        {/* Hero - Clean, minimal */}
        <div className="pt-6 pb-4">
          <div className="flex items-start gap-4">
            <div className="flex-1">
              <h1 className="font-bold mb-2" style={{ color: BRAND.navy, fontSize: '24px', lineHeight: '1.25' }} data-testid="hero-headline">Check my deal</h1>
              <p className="text-sm mb-3" style={{ color: '#5C6370', lineHeight: '1.6' }}>Added fees and add-ons are one of the easiest ways to overpay. Select what you see on your paperwork so you can spot red flags and push back before you sign.</p>
              {/* Trust strip - navy outline */}
              <div className="flex flex-wrap gap-2 mt-3">
                <span style={{ backgroundColor: '#FFFFFF', border: `1px solid ${BRAND.navy}`, borderRadius: '999px', fontSize: '12px', padding: '6px 12px', color: BRAND.navy, cursor: 'default' }}>Education only</span>
                <span style={{ backgroundColor: '#FFFFFF', border: `1px solid ${BRAND.navy}`, borderRadius: '999px', fontSize: '12px', padding: '6px 12px', color: BRAND.navy, cursor: 'default' }}>No commissions</span>
                <span style={{ backgroundColor: '#FFFFFF', border: `1px solid ${BRAND.navy}`, borderRadius: '999px', fontSize: '12px', padding: '6px 12px', color: BRAND.navy, cursor: 'default' }}>Private by design</span>
              </div>
            </div>
            <div className="flex-shrink-0 hidden sm:block"><DealCheckIcon /></div>
          </div>
        </div>

        {/* Steps card - NOT clickable, static display */}
        <div className="mb-5 p-4" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '14px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
          <style>{`
            .step-card-static {
              cursor: default !important;
              pointer-events: none;
            }
            .step-card-static button, .step-card-static a {
              pointer-events: auto;
            }
          `}</style>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Step 1 */}
            <div className="p-4 step-card-static" style={{ backgroundColor: '#FAFBFC', border: '1px solid #E5E7EB', borderRadius: '12px' }}>
              <div className="flex items-start gap-3">
                <IconTile size="sm">
                  <FileText className="w-4 h-4" style={{ color: BRAND.navy }} />
                </IconTile>
                <div>
                  <p className="text-xs font-semibold mb-1" style={{ color: BRAND.navy }}>Ask for itemized OTD</p>
                  <p className="text-xs" style={{ color: '#6B7280', lineHeight: '1.6' }}>Ask for two documents in writing: (1) the Buyer's Order (or Purchase Agreement) and (2) the Finance Agreement (Retail Installment Contract) or Lease Contract. Both should show the out-the-door total and list each fee/add-on line-by-line. <button onClick={() => setShowItemizedTooltip(!showItemizedTooltip)} style={{ color: BRAND.navy, textDecoration: 'underline', textUnderlineOffset: '2px', fontWeight: 600, cursor: 'pointer', pointerEvents: 'auto' }}>What does itemized mean?</button></p>
                </div>
              </div>
            </div>
            {/* Step 2 */}
            <div className="p-4 step-card-static" style={{ backgroundColor: '#FAFBFC', border: '1px solid #E5E7EB', borderRadius: '12px' }}>
              <div className="flex items-start gap-3">
                <IconTile size="sm">
                  <ListChecks className="w-4 h-4" style={{ color: BRAND.navy }} />
                </IconTile>
                <div>
                  <p className="text-xs font-semibold mb-1" style={{ color: BRAND.navy }}>Tap what you see</p>
                  <p className="text-xs" style={{ color: '#6B7280', lineHeight: '1.6' }}>Match your paperwork, line by line. Either search a single line item (like "doc fee") or browse by category below. Select everything you see so you can tell what's required, what's normal, and what's optional.</p>
                </div>
              </div>
            </div>
            {/* Step 3 */}
            <div className="p-4 step-card-static" style={{ backgroundColor: '#FAFBFC', border: '1px solid #E5E7EB', borderRadius: '12px' }}>
              <div className="flex items-start gap-3">
                <IconTile size="sm">
                  <MessageSquare className="w-4 h-4" style={{ color: BRAND.navy }} />
                </IconTile>
                <div>
                  <p className="text-xs font-semibold mb-1" style={{ color: BRAND.navy }}>Get scripts</p>
                  <p className="text-xs" style={{ color: '#6B7280', lineHeight: '1.6' }}>Use polite and firm scripts to ask what it is, why it's there, and whether it's required. If it's optional, you'll have exact wording to remove it or negotiate it.</p>
                </div>
              </div>
            </div>
            {/* Step 4 */}
            <div className="p-4 step-card-static" style={{ backgroundColor: '#FAFBFC', border: '1px solid #E5E7EB', borderRadius: '12px' }}>
              <div className="flex items-start gap-3">
                <IconTile size="sm">
                  <CheckCircle2 className="w-4 h-4" style={{ color: BRAND.navy }} />
                </IconTile>
                <div>
                  <p className="text-xs font-semibold mb-1" style={{ color: BRAND.navy }}>Verify totals</p>
                  <p className="text-xs" style={{ color: '#6B7280', lineHeight: '1.6' }}>Do a quick math check before signing: confirm the out-the-door total equals vehicle price + taxes + DMV fees + dealer fees + any add-ons you agreed to. If a line item isn't itemized or wasn't discussed, pause and ask for a corrected, itemized sheet.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Two easy ways guide - navy outline, ABOVE search, visually distinct from Steps */}
        <div className="mb-5 p-4" style={{ backgroundColor: '#FFFFFF', border: `2px solid ${BRAND.navy}`, borderRadius: '12px' }}>
          <p className="font-semibold mb-2" style={{ color: BRAND.navy, fontSize: '14px', lineHeight: '1.55' }}>Two easy ways to use this:</p>
          <p style={{ color: '#5C6370', fontSize: '13px', lineHeight: '1.55', marginBottom: '4px' }}>Option 1 — Search a line item: type exactly what you see (example: doc fee, GAP, nitrogen tires).</p>
          <p style={{ color: '#5C6370', fontSize: '13px', lineHeight: '1.55', marginBottom: '8px' }}>Option 2 — Browse by category: open a category below and tap every item that matches your paperwork.</p>
          <p style={{ color: '#9CA3AF', fontSize: '13px', lineHeight: '1.55' }}>You can select multiple items, then review them one-by-one with red flags and scripts.</p>
        </div>

        {/* Itemized tooltip */}
        {showItemizedTooltip && (
          <div className="mb-4 p-4 rounded-xl" style={{ backgroundColor: '#F0F9FF', border: '1px solid #BAE6FD' }}>
            <div className="flex justify-between items-start mb-2">
              <p className="text-sm font-semibold" style={{ color: '#0369A1' }}>What does itemized mean?</p>
              <button onClick={() => setShowItemizedTooltip(false)}><X className="w-4 h-4" style={{ color: '#0369A1' }} /></button>
            </div>
            <p className="text-xs" style={{ color: '#0C4A6E', lineHeight: '1.5' }}>Ask the dealer for an out-the-door price with every fee and add-on listed separately on its own line. This makes it easier to see what you are being charged and question anything that looks off.</p>
          </div>
        )}

        {/* Checklist - Selection area */}
        <div style={{ marginTop: '16px' }}>
          <h2 className="text-base font-semibold mb-1" style={{ color: BRAND.navy }}>Option 1 — Search a line item</h2>
          <p className="text-sm mb-4" style={{ color: '#6B7280' }}>Type any fee or add-on exactly as it appears on your paperwork (example: doc fee, GAP, nitrogen tires).</p>
          
          {/* Search */}
          <div className="relative mb-5">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#9CA3AF' }} />
            <input type="text" placeholder="Search (e.g., GAP, LoJack, doc fee, nitrogen)" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full py-3 pl-10 pr-4 rounded-xl text-sm" style={{ border: '1px solid #E5E7EB', backgroundColor: '#FFFFFF', outline: 'none', color: BRAND.navy }} data-testid="search-input" />
          </div>

          {!isSearching ? (
            <>
              {/* Option 2 — Browse by category label */}
              <div className="mb-4">
                <h2 className="text-base font-semibold mb-1" style={{ color: BRAND.navy }}>Option 2 — Browse by category</h2>
                <p className="text-sm mb-4" style={{ color: '#6B7280' }}>Open a category, tap everything you see on your paperwork, then review each item for what it is and what to say.</p>
              </div>

              {/* Must be itemized section - TWO helper lines only */}
              <SectionHeader label="Must be itemized" helperLines={[
                "If it is not broken down line-by-line, ask for itemization before agreeing to pay it.",
                "Start with taxes and DMV fees first — they should be clearly listed and match your state."
              ]} />
              <div className="space-y-3 mb-4">
                {itemizedBuckets.map(bucket => {
                  const items = getItemsByBucket(bucket.id);
                  const selCount = items.filter(i => selectedItems.has(i.key)).length;
                  return (
                    <div key={bucket.id}>
                      <BucketCard bucket={bucket} itemCount={items.length} selectedCount={selCount} isExpanded={expandedBuckets.has(bucket.id)} onClick={() => handleBucketClick(bucket.id)} />
                      <BucketSection ref={el => bucketRefs.current[bucket.id] = el} bucket={bucket} items={items} selectedItems={selectedItems} onToggle={toggleItem} isExpanded={expandedBuckets.has(bucket.id)} />
                    </div>
                  );
                })}
              </div>

              {/* Profit-center add-ons section */}
              <SectionHeader label="Profit-center add-ons" helperLines={["These are optional products sold for profit — only keep what you truly want at a fair price."]} />
              <div className="space-y-3">
                {addonBuckets.map(bucket => {
                  const items = getItemsByBucket(bucket.id);
                  const selCount = items.filter(i => selectedItems.has(i.key)).length;
                  return (
                    <div key={bucket.id}>
                      <BucketCard bucket={bucket} itemCount={items.length} selectedCount={selCount} isExpanded={expandedBuckets.has(bucket.id)} onClick={() => handleBucketClick(bucket.id)} />
                      <BucketSection ref={el => bucketRefs.current[bucket.id] = el} bucket={bucket} items={items} selectedItems={selectedItems} onToggle={toggleItem} isExpanded={expandedBuckets.has(bucket.id)} />
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="space-y-4">
              {bucketsWithMatches.length > 0 ? bucketsWithMatches.map(bucket => {
                const matchingItems = filteredItems.filter(i => i.bucket === bucket.id);
                return (
                  <div key={bucket.id}>
                    <h3 className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: '#9CA3AF' }}>{bucket.title}</h3>
                    <div className="space-y-2">
                      {matchingItems.map(item => <ChecklistItem key={item.key} item={item} isSelected={selectedItems.has(item.key)} onToggle={toggleItem} />)}
                    </div>
                  </div>
                );
              }) : (
                <div className="text-center py-8">
                  <p className="text-sm mb-2" style={{ color: '#6B7280' }}>No matches found</p>
                  <p className="text-xs" style={{ color: '#9CA3AF' }}>Try another term (example: LoJack, GAP, doc fee)</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Review Section */}
        {selectedCount > 0 && (
          <div ref={reviewSectionRef} className="mb-5 scroll-mt-4 transition-all duration-300" style={{ backgroundColor: '#FFFFFF', borderRadius: '16px', boxShadow: '0 2px 10px rgba(0,0,0,0.06)', border: '1px solid #E5E7EB', padding: '24px' }} data-testid="review-section">
            {viewingItem ? (
              <ItemDetailView item={viewingItem} onBack={() => setViewingItem(null)} />
            ) : (
              <>
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-base font-bold" style={{ color: BRAND.navy }}>Review your selected items</h2>
                  <span className="px-2.5 py-1 text-xs font-semibold rounded-full" style={{ backgroundColor: '#E0F2FE', color: '#0369A1' }}>{selectedCount} selected</span>
                </div>
                <p className="text-sm mb-5" style={{ color: '#6B7280' }}>Go line by line and ask for each item to be explained or removed.</p>
                <div className="space-y-3">{selectedItemsArray.map(item => <ReviewCard key={item.key} item={item} onReview={setViewingItem} onRemove={removeItem} />)}</div>
              </>
            )}
          </div>
        )}

        {/* Divider before Help the next buyer */}
        <div style={{ marginTop: '28px', marginBottom: '16px', borderTop: '1px solid rgba(0,0,0,0.06)' }} />

        {/* Help the Next Buyer - Premium feature card */}
        <div className="mb-5" style={{ backgroundColor: '#FFFFFF', borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,31,63,0.06)', border: '1px solid rgba(0,31,63,0.10)', padding: '24px' }} data-testid="help-next-buyer-section">
          <div className="flex items-start gap-4 mb-4">
            <IconTile size="md">
              <Share2 className="w-5 h-5" style={{ color: BRAND.navy }} strokeWidth={1.5} />
            </IconTile>
            <div>
              <h2 className="font-semibold mb-2" style={{ color: BRAND.navy, fontSize: '18px' }}>Help the next buyer</h2>
              <p style={{ color: '#6B7280', lineHeight: '1.6', fontSize: '15px' }}>Optional: share a simple snapshot of your deal so VINdicated can publish fair price ranges over time.</p>
              <p className="mt-2" style={{ color: '#9CA3AF', fontSize: '13px', lineHeight: '1.5' }}>Shared snapshots are aggregated and never displayed as a single person's exact deal.</p>
            </div>
          </div>
          <button onClick={() => {}} className="w-full flex items-center justify-center gap-2 text-sm font-semibold transition-colors" style={{ backgroundColor: '#FFFFFF', border: `2px solid ${BRAND.navy}`, color: BRAND.navy, borderRadius: '14px', minHeight: '52px', padding: '12px 16px', boxShadow: '0 1px 3px rgba(0,31,63,0.08)' }} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(0,31,63,0.04)'; e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,31,63,0.12)'; }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#FFFFFF'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,31,63,0.08)'; }} data-testid="share-deal-btn">
            <Share2 className="w-4 h-4" style={{ color: BRAND.navy }} />Share my deal snapshot (optional)
          </button>
        </div>

        {/* Trust Box */}
        <div className="mb-6 p-4" style={{ backgroundColor: '#FFFFFF', border: '1px solid rgba(0,31,63,0.10)', borderRadius: '16px' }}>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#F6F8FF', border: '1px solid rgba(0,31,63,0.10)' }}>
              <Shield className="w-5 h-5" style={{ color: BRAND.navy }} strokeWidth={1.8} />
            </div>
            <div>
              <p className="font-bold mb-1" style={{ color: BRAND.navy, fontSize: '15px' }}>100% educational. 0% commissions.</p>
              <p style={{ color: '#5C6370', fontSize: '13px', lineHeight: '1.55' }}>We are independent — not paid by dealers, lenders, or any brand you see in the app. No commissions. No referral fees. No data selling. Educational only — not financial, legal, or tax advice. No guarantees.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Sticky Bar */}
      {selectedCount > 0 && !viewingItem && (
        <div className="fixed bottom-0 left-0 right-0 p-4" style={{ backgroundColor: '#FFFFFF', borderTop: '1px solid #E5E7EB', boxShadow: '0 -2px 8px rgba(0,0,0,0.04)', zIndex: 100, marginBottom: '60px' }} data-testid="sticky-bar">
          <div className="max-w-screen-sm mx-auto">
            <button onClick={scrollToReview} className="w-full py-3.5 rounded-xl text-sm font-semibold transition-all" style={{ backgroundColor: BRAND.navy, color: '#FFFFFF' }} data-testid="review-selected-btn">
              Review selected ({selectedCount})
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScanIntro;
